import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { resolveMediaUrl } from '../lib/api';
import { startRecorder, isEmptyRecording, uploadErrorMessage, postAudio, fileNameFor } from '../lib/recording';
import { NoAudioPanel } from '../components/speaking/NoAudioPanel';
import { Volume2, Square, SkipBack, SkipForward, Play, Pause, AlertTriangle } from 'lucide-react';
import { RichText } from '../components/RichTextEditor';
import { useAuth } from '../contexts/AuthContext';
import { ProtectedContent } from '../components/ContentProtection';
import { triggerExitIntent } from '../lib/exitIntent';
import '../styles/exam-mobile.css';

/* ═══════════════════════════════════════════════════════════════
   CELPIP EXACT COLORS — from official test screenshots
   ═══════════════════════════════════════════════════════════════ */
const S = {
  hdrBg: '#dfe3e8', hdrBorder: '#c5c9ce', hdrText: '#2c3e50',
  blue: '#0b5cad', blueHover: '#094a8c', nextBg: '#0b5cad',
  timerRed: '#c0392b', infoBg: '#2980b9', white: '#fff',
  textDark: '#2c3e50', textBody: '#34495e', textLight: '#7f8c8d',
  border: '#c5c9ce', panelBorder: '#d5d8dc', lightBg: '#f4f6f8',
  // Reading-section authentic styling (matches official CELPIP look-and-feel)
  panelTint: '#f3f8fb',           // subtle sky-blue tint on the right (questions) panel
  qBoxBorder: '#a8c0d0',          // cyan-blue border around each question container
  qBoxBg: '#ffffff',              // clean off-white inside the question container
  qBoxRadius: '4px',
  ddDotted: '#94a3b8',            // dotted underline beneath each dropdown (CELPIP signature)
  font: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
};

/* Shared dotted-underline wrapper used across reading dropdowns to mimic
   the official CELPIP look (dotted line extending past the dropdown). */
const DD_WRAP = {
  display:'inline-block',
  borderBottom:'1px dotted #94a3b8',
  paddingBottom:'2px',
  minWidth:'120px',
  marginRight:'4px',
};

/* ─── Shared Components ──────────────────────────────────────── */
function InfoIcon() {
  return <span style={{ display:'inline-flex',alignItems:'center',justifyContent:'center',width:'22px',height:'22px',borderRadius:'50%',background:S.infoBg,color:'#fff',fontSize:'13px',fontWeight:'bold',marginRight:'10px',flexShrink:0 }}>i</span>;
}

function Header({ title, timeRemaining, onNext, showNext=true, onHome, rightExtra }) {
  const fmtTime = () => {
    if (timeRemaining == null || timeRemaining < 0) return null;
    const m = Math.floor(timeRemaining / 60);
    const s = timeRemaining % 60;
    if (m > 0 && s > 0) return `${m} minute${m!==1?'s':''} ${s} second${s!==1?'s':''}`;
    if (m > 0) return `${m} minute${m!==1?'s':''}`;
    return `${s} second${s!==1?'s':''}`;
  };
  const timeStr = fmtTime();
  const shortTime = (timeRemaining == null || timeRemaining < 0) ? null : `${Math.floor(timeRemaining / 60)}:${String(timeRemaining % 60).padStart(2, '0')}`;
  return (
    <div style={{fontFamily:S.font}} data-testid="celpip-header-wrap">
      <div className="exam-topbar" style={{background:'#1a1f2e',padding:'5px 16px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <div style={{display:'flex',alignItems:'center',gap:'8px',cursor:'pointer'}} onClick={onHome}>
          <img src="/logo-navbar-dark.png" alt="CELPIPMax" style={{width:'22px',height:'22px'}} />
          <span style={{color:'#fff',fontWeight:'600',fontSize:'13px'}}>CELPIPMax</span>
        </div>
        <div style={{display:'flex',gap:'14px'}}>
          <span style={{color:'#94a3b8',fontSize:'12px',cursor:'pointer'}} onClick={onHome}>Dashboard</span>
          <span style={{color:'#94a3b8',fontSize:'12px',cursor:'pointer'}} onClick={onHome}>Exit</span>
        </div>
      </div>
      <div className="exam-hdr" style={{background:S.hdrBg,borderBottom:`1px solid ${S.hdrBorder}`,padding:'7px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',minHeight:'42px'}} data-testid="celpip-header">
        <span className="exam-hdr-title" style={{fontWeight:'600',fontSize:'14px',color:S.hdrText}}>{title}</span>
        <div className="exam-hdr-right" style={{display:'flex',alignItems:'center',gap:'14px'}}>
          {rightExtra && <span className="exam-hdr-extra">{rightExtra}</span>}
          {timeStr && (
            <span style={{fontSize:'14px',color:S.hdrText}} data-testid="celpip-timer">
              <span className="exam-time-long">Time remaining: <strong style={{color:S.timerRed}}>{timeStr}</strong></span>
              <span className="exam-time-short">Time left: <strong style={{color:S.timerRed}}>{shortTime}</strong></span>
            </span>
          )}
          {showNext && <button onClick={onNext} style={{background:S.nextBg,color:'#fff',border:'none',padding:'6px 20px',fontWeight:'bold',fontSize:'13px',cursor:'pointer',fontFamily:S.font,borderRadius:'2px'}} data-testid="celpip-next-btn">NEXT</button>}
        </div>
      </div>
    </div>
  );
}

const stripTags = (v) => String(v || '').replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();

/* Rows for the practice-test "Answer Key" (correct answer + the student's pick for
   every scored item currently on screen). */
function answerKeyRows(questions, responses) {
  const rows = [];
  let n = 0;
  for (const q of questions || []) {
    const opts = q.options || [];
    const optText = (id) => stripTags((opts.find((o) => o.id === id) || {}).text) || id || '—';
    if (q.question_type === 'dropdown_paragraph') {
      const picks = (responses?.[q.id]?.selected_options) || {};
      (q.statements || []).forEach((s) => {
        n += 1;
        const pick = picks[s.id] || '';
        rows.push({ key: `${q.id}-${s.id}`, n, prompt: stripTags(s.text), correct: s.correct_option_id ? optText(s.correct_option_id) : '—', yours: pick ? optText(pick) : '', ok: !!pick && pick === s.correct_option_id, explanation: '' });
      });
    } else if (q.question_type === 'mcq' || q.question_type === 'dropdown_fill' || q.correct_answer) {
      n += 1;
      const sel = responses?.[q.id]?.selected_option || '';
      rows.push({ key: q.id, n, prompt: stripTags(q.prompt_text), correct: q.correct_answer ? optText(q.correct_answer) : '—', yours: sel ? optText(sel) : '', ok: !!sel && sel === q.correct_answer, explanation: stripTags(q.explanation) });
    }
  }
  return rows;
}

function AnswerKeyPanel({ questions, responses, onClose }) {
  const rows = answerKeyRows(questions, responses);
  return (
    <div style={{position:'fixed',inset:0,zIndex:60,background:'rgba(15,23,42,0.45)',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:S.font}} onClick={onClose} data-testid="answer-key-overlay">
      <div style={{background:'#fff',width:'min(720px, 94vw)',maxHeight:'84vh',display:'flex',flexDirection:'column',borderRadius:'4px',border:`1px solid ${S.hdrBorder}`,boxShadow:'0 20px 60px rgba(0,0,0,0.25)'}} onClick={(e)=>e.stopPropagation()} data-testid="answer-key-panel">
        <div style={{background:S.hdrBg,borderBottom:`1px solid ${S.hdrBorder}`,padding:'9px 16px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <span style={{fontWeight:'700',fontSize:'14px',color:S.hdrText}}>Answer Key</span>
          <button onClick={onClose} style={{background:S.nextBg,color:'#fff',border:'none',padding:'5px 16px',fontWeight:'bold',fontSize:'12px',cursor:'pointer',borderRadius:'2px',fontFamily:S.font}} data-testid="answer-key-close">CLOSE</button>
        </div>
        <div style={{padding:'14px 16px',overflowY:'auto'}}>
          <p style={{fontSize:'12px',color:S.textLight,margin:'0 0 10px'}}>Correct answers for the questions on this screen. This button does not appear in the official test.</p>
          {rows.length === 0 ? <p style={{fontSize:'13px',color:S.textLight}} data-testid="answer-key-empty">No scored answers on this screen.</p> : (
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px'}} data-testid="answer-key-table">
              <thead><tr style={{textAlign:'left',color:S.textLight,fontSize:'11px',textTransform:'uppercase',letterSpacing:'0.04em'}}><th style={{padding:'6px 8px',width:'40px'}}>#</th><th style={{padding:'6px 8px'}}>Correct answer</th><th style={{padding:'6px 8px'}}>Your answer</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} style={{borderTop:'1px solid #e5e7eb'}} data-testid={`answer-key-row-${r.n}`}>
                    <td style={{padding:'8px',color:S.textBody,verticalAlign:'top',fontWeight:'600'}}>{r.n}</td>
                    <td style={{padding:'8px',verticalAlign:'top'}}>
                      <div style={{color:'#166534',fontWeight:'600'}} data-testid={`answer-key-correct-${r.n}`}>{r.correct}</div>
                      {r.prompt && <div style={{color:S.textLight,fontSize:'11px',marginTop:'2px'}}>{r.prompt.length > 110 ? `${r.prompt.slice(0, 110)}…` : r.prompt}</div>}
                      {r.explanation && <div style={{color:S.textBody,fontSize:'12px',marginTop:'4px',fontStyle:'italic'}}>{r.explanation}</div>}
                    </td>
                    <td style={{padding:'8px',verticalAlign:'top',color:r.yours ? (r.ok ? '#27ae60' : '#c0392b') : '#999'}} data-testid={`answer-key-yours-${r.n}`}>{r.yours || 'not answered'}{r.yours ? (r.ok ? ' ✓' : ' ✗') : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function Foot({ onBack, showBack=true, showAnswerKey=false, onAnswerKey, showSubmit=false, onSubmit, answerKeyQuestions, responses }) {
  const [keyOpen, setKeyOpen] = useState(false);
  const openKey = () => { if (onAnswerKey) onAnswerKey(); else setKeyOpen(true); };
  return (
    <div className="exam-foot" style={{background:S.hdrBg,borderTop:`1px solid ${S.hdrBorder}`,padding:'7px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',minHeight:'38px',fontFamily:S.font}} data-testid="celpip-footer">
      <div style={{display:'flex',gap:'8px'}}>
        {showAnswerKey && <button onClick={openKey} style={{background:'#f0f0f0',border:'1px solid #aaa',padding:'5px 16px',fontSize:'12px',cursor:'pointer',fontFamily:S.font,borderRadius:'2px'}} data-testid="answer-key-btn">Answer Key</button>}
      </div>
      {keyOpen && <AnswerKeyPanel questions={answerKeyQuestions || []} responses={responses || {}} onClose={()=>setKeyOpen(false)}/>}
      <div style={{display:'flex',gap:'8px'}}>
        {showSubmit && <button onClick={onSubmit} style={{background:'#27ae60',color:'#fff',border:'none',padding:'6px 20px',fontWeight:'bold',fontSize:'13px',cursor:'pointer',fontFamily:S.font,borderRadius:'2px'}} data-testid="submit-exam-btn">SUBMIT</button>}
        {showBack && <button onClick={onBack} style={{background:S.nextBg,color:'#fff',border:'none',padding:'6px 20px',fontWeight:'bold',fontSize:'13px',cursor:'pointer',fontFamily:S.font,borderRadius:'2px'}} data-testid="celpip-back-btn">BACK</button>}
      </div>
    </div>
  );
}

/* ─── Mid-exam Section Result helpers ──────────────────────────── */
const SKILL_TITLE = { listening: 'Listening', reading: 'Reading', writing: 'Writing', speaking: 'Speaking' };
const PART_LETTER_SKILL = { L: 'listening', R: 'reading', W: 'writing', S: 'speaking' };

/* Builds a CELPIP-style section heading, e.g. "Listening Part 3: Listening for
   Information" or "Speaking Task 5: Comparing and Persuading". Listening/Reading
   use "Part N", Writing/Speaking use "Task N" (N from the part_id, L3→3, S5→5).
   If the section name was already manually prefixed, it is returned unchanged
   so the designation isn't duplicated. Structural parts (LO/LE/…) and any
   part_id without a number fall back to the plain section name. */
function partAwareTitle(sec) {
  const nm = (sec?.name || '').trim();
  const pid = sec?.part_id || '';
  const m = pid.match(/^([LRWS])(\d+)$/i);
  if (!m) return nm;
  const skill = sec?.skill_id || PART_LETTER_SKILL[m[1].toUpperCase()];
  const SK = SKILL_TITLE[skill];
  if (!SK) return nm;
  const unit = (skill === 'writing' || skill === 'speaking') ? 'Task' : 'Part';
  const num = parseInt(m[2], 10);
  if (new RegExp(`^${SK}\\s+(Part|Task)\\s+\\d+`, 'i').test(nm)) return nm;
  return nm ? `${SK} ${unit} ${num}: ${nm}` : `${SK} ${unit} ${num}`;
}

function clbColor(n) {
  if (n == null) return '#7f8c8d';
  if (n >= 9) return '#27ae60';
  if (n >= 7) return '#2980b9';
  if (n >= 5) return '#f39c12';
  return '#e74c3c';
}

function hasWritingOrSpeaking(skills) {
  return (skills || []).some(s => s === 'writing' || s === 'speaking');
}

function flattenRowsLite(questions) {
  const rows = [];
  let counter = 0;
  for (const q of questions || []) {
    if (q.question_type === 'mcq' || q.question_type === 'dropdown_fill') {
      counter += 1;
      rows.push({ key: q.id, index: counter, correctText: q.correct_text || '—', userText: q.user_answer || '', isCorrect: !!q.is_correct, hasUserAnswer: !!q.user_answer });
    } else if (q.question_type === 'dropdown_paragraph' && Array.isArray(q.statement_results)) {
      for (const s of q.statement_results) {
        counter += 1;
        rows.push({ key: `${q.id}-${s.statement_id || counter}`, index: counter, correctText: s.correct_option_text || s.correct_option_id || '—', userText: s.selected_option_text || '', isCorrect: !!s.is_correct, hasUserAnswer: !!s.selected_option_id });
      }
    }
  }
  return rows;
}

/* Mid-exam result/score interstitial — rendered for a `section_result` flow
   step. Calls the non-finalizing /section-result endpoint, shows a
   "scoring… please wait" spinner while Writing/Speaking are AI-graded, and a
   Continue button that is always available. */
function SectionResultPanel({ examId, attemptId, responses, skills, heading, title, onContinue, onHome, isPreview }) {
  const preview = isPreview || !attemptId || attemptId === 'preview';
  const [loading, setLoading] = useState(!preview);
  const [data, setData] = useState(null);
  const [demoMode, setDemoMode] = useState(false);
  const [openKeys, setOpenKeys] = useState({});
  const startedRef = useRef(false);
  const mountedRef = useRef(true);

  // Track mount status so we never call setState after unmount.
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Fetch the partial result exactly once (guarded by startedRef) — listing all
  // referenced props as deps keeps the hooks dependency linter satisfied without
  // a rule-named lint suppression (which breaks the CI production build).
  useEffect(() => {
    if (preview || startedRef.current) return;
    startedRef.current = true;
    setLoading(true);
    api.get('/api/health').then(r => { if (mountedRef.current) setDemoMode(r?.data?.services?.azure_openai === false); }).catch(() => {});
    api.post(`/api/exams/${examId}/section-result/${attemptId}`, { responses: responses || {}, skills: skills || [] })
      .then(r => { if (mountedRef.current) setData(r.data); })
      .catch(() => { if (mountedRef.current) setData({ error: true }); })
      .finally(() => { if (mountedRef.current) setLoading(false); });
  }, [preview, examId, attemptId, responses, skills]);

  const SKILL_LABELS = { listening: 'Listening', reading: 'Reading', writing: 'Writing', speaking: 'Speaking' };
  const ss = data?.skill_scores || {};
  const ak = data?.answer_key || [];
  const dr = data?.detailed_responses || [];
  const scopeSkills = (skills && skills.length) ? skills : (data?.scope_skills || []);
  const taskNum = (pid) => { const m = (pid || '').match(/(\d+)/); return m ? parseInt(m[1], 10) : null; };
  const wResps = dr.filter(d => d.response_type === 'text_response').sort((a, b) => (taskNum(a.part_id) || 0) - (taskNum(b.part_id) || 0));
  const sResps = dr.filter(d => d.response_type === 'audio_response').sort((a, b) => (taskNum(a.part_id) || 0) - (taskNum(b.part_id) || 0));

  const shell = (children) => (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: S.lightBg, fontFamily: S.font }} data-testid="section-result-panel">
      <Header title={title} showNext={false} onHome={onHome} />
      <div style={{ flex: 1, overflowY: 'auto', padding: '28px 16px' }}>
        <div style={{ maxWidth: '860px', margin: '0 auto' }}>{children}</div>
      </div>
      <div style={{ background: S.hdrBg, borderTop: `1px solid ${S.hdrBorder}`, padding: '10px 16px', display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={onContinue} data-testid="section-result-continue-btn" style={{ background: '#27ae60', color: '#fff', border: 'none', padding: '9px 30px', fontWeight: 'bold', fontSize: '14px', cursor: 'pointer', borderRadius: '3px', fontFamily: S.font }}>Continue →</button>
      </div>
    </div>
  );

  if (preview) {
    return shell(
      <div style={{ background: S.white, border: `1px solid ${S.hdrBorder}`, borderRadius: '8px', padding: '32px', textAlign: 'center' }}>
        <div style={{ fontSize: '15px', fontWeight: 'bold', color: S.textDark, marginBottom: '8px' }}>{heading || 'Result / Score Page'}</div>
        <p style={{ fontSize: '13px', color: S.textLight }}>In a real attempt, students see their {(scopeSkills.length ? scopeSkills : ['completed']).map(s => SKILL_LABELS[s] || s).join(', ')} score here, then click Continue.</p>
      </div>
    );
  }

  if (loading) {
    return shell(
      <div style={{ background: S.white, border: `1px solid ${S.hdrBorder}`, borderRadius: '8px', padding: '48px', textAlign: 'center' }} data-testid="section-result-loading">
        <div style={{ width: '40px', height: '40px', border: '4px solid #ddd', borderTop: `4px solid ${S.blue}`, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 18px' }} />
        <p style={{ fontSize: '15px', color: S.textBody, fontWeight: '600' }}>Scoring your responses… please wait</p>
        <p style={{ fontSize: '12px', color: S.textLight, marginTop: '6px' }}>Listening &amp; Reading are instant. Writing &amp; Speaking are graded by AI and may take a moment.</p>
        <style>{`@keyframes spin{to{transform:rotate(360deg);}}`}</style>
      </div>
    );
  }

  const nothingToShow = Object.keys(ss).length === 0 && wResps.length === 0 && sResps.length === 0;

  return shell(
    <div>
      <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: S.textDark, marginBottom: '4px' }}>{heading || 'Your Results So Far'}</h2>
      <p style={{ fontSize: '13px', color: S.textLight, marginBottom: '20px' }}>This is a checkpoint — your exam is not finished. Review your score, then click Continue.</p>

      {demoMode && hasWritingOrSpeaking(scopeSkills) && (
        <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', color: '#78350f', padding: '10px 14px', borderRadius: '6px', fontSize: '12px', marginBottom: '18px' }}>
          AI scoring is in demo mode — Writing &amp; Speaking scores are illustrative only.
        </div>
      )}

      {['listening', 'reading'].filter(sk => ss[sk]).map(sk => (
        <div key={sk} style={{ background: S.white, border: `1px solid ${S.hdrBorder}`, borderRadius: '8px', marginBottom: '16px', overflow: 'hidden' }} data-testid={`section-result-${sk}`}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px' }}>
            <div>
              <div style={{ fontSize: '12px', fontWeight: '700', color: S.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{SKILL_LABELS[sk]}</div>
              <div style={{ fontSize: '13px', color: S.textBody, marginTop: '4px' }}>{ss[sk].correct} / {ss[sk].total} correct</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '34px', fontWeight: 800, color: clbColor(ss[sk].clb), lineHeight: 1 }} data-testid={`section-result-clb-${sk}`}>CLB {ss[sk].clb}</div>
              <div style={{ fontSize: '11px', color: S.textLight }}>Approximate</div>
            </div>
          </div>
          {ak.filter(s => s.skill_id === sk).length > 0 && (
            <div style={{ borderTop: `1px solid ${S.hdrBorder}` }}>
              <button onClick={() => setOpenKeys(p => ({ ...p, [sk]: !p[sk] }))} data-testid={`section-result-toggle-${sk}`} style={{ width: '100%', textAlign: 'left', padding: '10px 20px', background: '#f8f9fa', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '600', color: S.blue }}>
                {openKeys[sk] ? '▾ Hide answer key' : '▸ Show answer key'}
              </button>
              {openKeys[sk] && (
                <div style={{ padding: '0 20px 16px' }}>
                  {ak.filter(s => s.skill_id === sk).map((sec, si) => (
                    <div key={si} style={{ marginTop: '12px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 'bold', color: S.textDark, marginBottom: '6px' }}>{sec.name}</div>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                        <tbody>
                          {flattenRowsLite(sec.questions).map(row => (
                            <tr key={row.key} style={{ borderBottom: '1px solid #eee' }}>
                              <td style={{ padding: '5px 6px', color: S.textBody, width: '70px' }}>Q{row.index}</td>
                              <td style={{ padding: '5px 6px', color: S.textBody }}>{row.correctText}</td>
                              <td style={{ padding: '5px 6px', color: row.isCorrect ? '#27ae60' : row.hasUserAnswer ? '#c0392b' : '#999' }}>{row.userText || '—'}{row.hasUserAnswer ? (row.isCorrect ? ' ✓' : ' ✗') : ''}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {['writing', 'speaking'].filter(sk => scopeSkills.includes(sk)).map(sk => {
        const resps = sk === 'writing' ? wResps : sResps;
        if (resps.length === 0) return null;
        const combined = ss[sk]?.clb;
        const label = sk === 'writing' ? 'Writing' : 'Speaking';
        return (
          <div key={sk} style={{ background: S.white, border: `1px solid ${S.hdrBorder}`, borderRadius: '8px', marginBottom: '16px', overflow: 'hidden' }} data-testid={`section-result-${sk}`}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', borderBottom: `1px solid ${S.hdrBorder}` }}>
              <div style={{ fontSize: '12px', fontWeight: '700', color: S.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label} (Estimated)</div>
              <div style={{ fontSize: '30px', fontWeight: 800, color: clbColor(combined), lineHeight: 1 }} data-testid={`section-result-clb-${sk}`}>{combined ? `CLB ${combined}` : '—'}</div>
            </div>
            <div style={{ padding: '12px 20px' }}>
              {resps.map((r, idx) => {
                const fb = (r.ai_feedback && typeof r.ai_feedback === 'object') ? r.ai_feedback : null;
                const score = (fb && typeof fb.overall_clb === 'number') ? fb.overall_clb : (typeof r.ai_score === 'number' ? r.ai_score : null);
                const tnum = taskNum(r.part_id) || idx + 1;
                const sug = (fb?.key_suggestions || [])[0] || '';
                return (
                  <div key={r.question_id || idx} style={{ padding: '10px 0', borderBottom: idx < resps.length - 1 ? '1px solid #eee' : 'none' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: S.textDark }}>{label} Task {tnum}</span>
                      <span style={{ fontSize: '14px', fontWeight: 'bold', color: clbColor(score) }}>{score != null ? `Est. CLB ${score}` : 'Scoring…'}</span>
                    </div>
                    {sug && <p style={{ fontSize: '12px', color: S.textLight, marginTop: '4px' }}>{sug}</p>}
                  </div>
                );
              })}
              <p style={{ fontSize: '11px', color: S.textLight, marginTop: '10px' }}>Estimated — not an official CELPIP score. Full feedback appears in your final results.</p>
            </div>
          </div>
        );
      })}

      {nothingToShow && (
        <div style={{ background: S.white, border: `1px solid ${S.hdrBorder}`, borderRadius: '8px', padding: '32px', textAlign: 'center', color: S.textLight, fontSize: '13px' }}>No scored results to show for this checkpoint yet — click Continue to keep going.</div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════
   STEP RENDERERS — Each renders one screen type
   ═══════════════════════════════════════════════════════════════ */

/* ─── text_instruction ───────────────────────────────────────── */
function StepTextInstruction({step, title, testNum, onNext, onBack, onHome}) {
  const content = step.content || '';
  const isHtml = /<\/?(p|br|ul|ol|li|strong|b|em|i|u|div|span|h[1-6])\b/i.test(content);
  const lines = content.split('\n').filter(l=>l.trim());
  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',fontFamily:S.font}}>
      <Header title={`Practice Test ${testNum} - ${title}`} onNext={onNext} showNext={true} timeRemaining={null} onHome={onHome}/>
      <div className="exam-body" style={{flex:1,padding:'24px 32px',background:S.white,overflowY:'auto'}}>
        {step.heading && <div style={{fontWeight:'bold',fontSize:'17px',color:S.textDark,marginBottom:'20px'}}>{step.heading}</div>}
        {isHtml ? (
          <div style={{color:S.blue,fontSize:'15px',lineHeight:'1.7'}}><RichText value={content}/></div>
        ) : lines.length > 0 && (
          <ul style={{listStyle:'none',padding:0,margin:0}}>
            {lines.map((l,i)=>(
              <li key={i} style={{display:'flex',alignItems:'flex-start',gap:'8px',color:S.blue,fontSize:'15px',lineHeight:'1.7',padding:'8px 0',borderBottom:'1px solid #eee'}}>
                <span style={{color:S.blue,fontWeight:'bold',fontSize:'16px',marginTop:'2px'}}>&bull;</span>
                <span>{l}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <Foot onBack={onBack}/>
    </div>
  );
}

/* ─── video_instruction ──────────────────────────────────────── */
// Normalize share / watch URLs to their iframe-embeddable form.
// Loom, YouTube and Vimeo all refuse iframe embedding on their share URLs
// (X-Frame-Options: deny → "refused to connect"). We rewrite to the embed
// endpoint so admins can paste the URL straight from the browser bar.
function normalizeEmbedUrl(raw) {
  if (!raw) return '';
  const url = String(raw).trim();
  // Loom: https://www.loom.com/share/{id}[?sid=...]  →  /embed/{id}
  const loom = url.match(/loom\.com\/(?:share|embed)\/([a-zA-Z0-9]+)/);
  if (loom) return `https://www.loom.com/embed/${loom[1]}`;
  // YouTube: watch?v={id} | youtu.be/{id} | shorts/{id}  →  /embed/{id}
  const yt = url.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  // Vimeo: vimeo.com/{id}  →  player.vimeo.com/video/{id}
  const vm = url.match(/(?:^|\/\/)(?:www\.)?vimeo\.com\/(?!video\/)(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return url;
}

function StepVideoInstruction({step, title, testNum, onNext, onBack, onHome}) {
  const rawUrl = step.video_url || step.loom_url || '';
  const videoUrl = normalizeEmbedUrl(rawUrl);
  const [canNext, setCanNext] = React.useState(false);
  // Direct file (mp4/webm/ogg/mov) → HTML5 <video>. Otherwise iframe (YouTube/Loom/Vimeo embeds).
  const isDirectVideo = /\.(mp4|webm|ogg|mov)(\?|#|$)/i.test(videoUrl);
  const heading = step.heading || 'Watch the discussion. You will watch the discussion only once.';
  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',fontFamily:S.font}}>
      <Header title={`Practice Test ${testNum} - ${title}`} showNext={canNext || !videoUrl} onNext={onNext} timeRemaining={null} onHome={onHome}/>
      <div className="exam-body" style={{flex:1,padding:'24px 32px',background:S.white,overflowY:'auto'}}>
        <div style={{display:'flex',alignItems:'flex-start',marginBottom:'18px'}}>
          <InfoIcon/>
          <span style={{color:S.blue,fontSize:'14px',fontWeight:'600',lineHeight:'1.5'}}>{heading}</span>
        </div>
        <div style={{display:'flex',justifyContent:'center'}}>
          {videoUrl ? (
            <div style={{width:'100%',maxWidth:'720px',aspectRatio:'16/9',background:'#000',borderRadius:'4px',overflow:'hidden'}}>
              {isDirectVideo
                ? <video src={videoUrl} controls autoPlay style={{width:'100%',height:'100%'}} onEnded={()=>setCanNext(true)} onPlay={()=>setCanNext(true)} data-testid="section-video"/>
                : <iframe src={videoUrl} style={{width:'100%',height:'100%',border:'none'}} allow="autoplay; fullscreen" allowFullScreen title="Discussion Video" onLoad={()=>setCanNext(true)} data-testid="section-video-iframe"/>
              }
            </div>
          ) : (
            <div style={{width:'100%',maxWidth:'720px',aspectRatio:'16/9',background:'#000',borderRadius:'4px',display:'flex',alignItems:'center',justifyContent:'center'}}>
              <span style={{color:'#666',fontSize:'14px'}}>Video not configured</span>
            </div>
          )}
        </div>
      </div>
      <Foot onBack={onBack}/>
    </div>
  );
}

/* ─── context_image ──────────────────────────────────────────── */
function StepContextImage({step, title, testNum, onNext, onBack, onHome}) {
  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',fontFamily:S.font}}>
      <Header title={`Practice Test ${testNum} - ${title}`} onNext={onNext} timeRemaining={null} onHome={onHome}/>
      <div className="exam-body" style={{flex:1,padding:'24px 32px',background:S.white,overflowY:'auto'}}>
        <div style={{display:'flex',alignItems:'flex-start',marginBottom:'16px'}}>
          <InfoIcon/>
          <span style={{fontWeight:'bold',fontSize:'15px',color:S.blue}}>{step.heading || 'Instructions:'}</span>
        </div>
        {step.content && <div style={{fontSize:'15px',color:S.textBody,lineHeight:'1.7',fontStyle:'italic',margin:'0 0 20px 32px'}}><RichText value={step.content}/></div>}
        {step.image_url && <div style={{textAlign:'center'}}><img src={step.image_url} alt="Context" style={{maxWidth:'550px',maxHeight:'380px',borderRadius:'4px',border:'1px solid #ddd'}}/></div>}
      </div>
      <Foot onBack={onBack}/>
    </div>
  );
}

/* ─── audio_player (smooth) ──────────────────────────────────── */
function StepAudioPlayer({step, title, testNum, onNext, onBack, onHome, onAudioEnd}) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [curTime, setCurTime] = useState(0);
  const [dur, setDur] = useState(0);
  const [done, setDone] = useState(false);
  const audioRef = useRef(null);
  const simRef = useRef(null);
  const simState = useRef({paused:false, elapsed:0});
  const hasReal = !!(step.audio_url || '').trim();
  // Dev/admin escape hatch: ?devAudio=1 enables the legacy 12s simulated playback
  // when audio_url is missing. In production (no flag) we surface a clear error.
  const devAudio = (typeof window !== 'undefined') &&
    new URLSearchParams(window.location.search).get('devAudio') === '1';
  const showError = !hasReal && !devAudio;

  useEffect(() => {
    if (showError) return; // No playback when missing audio in production mode
    if (hasReal && audioRef.current) {
      const a = audioRef.current;
      const h1 = () => setDur(Math.floor(a.duration));
      const h2 = () => { if(a.duration){setCurTime(Math.floor(a.currentTime));setProgress((a.currentTime/a.duration)*100);} };
      const h3 = () => { setPlaying(false);setProgress(100);setDone(true);onAudioEnd?.(); };
      a.addEventListener('loadedmetadata',h1);a.addEventListener('timeupdate',h2);a.addEventListener('ended',h3);
      a.play().then(()=>setPlaying(true)).catch(()=>{});
      return ()=>{a.removeEventListener('loadedmetadata',h1);a.removeEventListener('timeupdate',h2);a.removeEventListener('ended',h3);};
    }
    // devAudio=1 fallback: simulated 12-second playback for admin/preview testing
    const simDur = 12;
    setDur(simDur); setPlaying(true); simState.current = {paused:false,elapsed:0};
    const tick = () => {
      if(simState.current.paused) return;
      simState.current.elapsed += 0.1;
      const e = simState.current.elapsed;
      setCurTime(Math.floor(e));
      setProgress(Math.min((e/simDur)*100,100));
      if(e >= simDur){clearInterval(simRef.current);setPlaying(false);setDone(true);onAudioEnd?.();}
    };
    simRef.current = setInterval(tick, 100);
    return ()=>clearInterval(simRef.current);
  }, [hasReal, showError]);

  const toggle = () => {
    if(hasReal&&audioRef.current){if(playing){audioRef.current.pause();setPlaying(false);}else{audioRef.current.play().then(()=>setPlaying(true)).catch(()=>{});}}
    else{if(playing){simState.current.paused=true;setPlaying(false);}else{simState.current.paused=false;setPlaying(true);}}
  };
  const seek = d => {
    if(hasReal&&audioRef.current){audioRef.current.currentTime=Math.max(0,Math.min(audioRef.current.duration,audioRef.current.currentTime+d));}
    else{simState.current.elapsed=Math.max(0,Math.min(dur,simState.current.elapsed+d));setCurTime(Math.floor(simState.current.elapsed));setProgress((simState.current.elapsed/dur)*100);}
  };
  const clickSeek = e => {
    const r=e.currentTarget.getBoundingClientRect();const p=Math.max(0,Math.min(1,(e.clientX-r.left)/r.width));
    if(hasReal&&audioRef.current){audioRef.current.currentTime=p*audioRef.current.duration;}
    else{simState.current.elapsed=p*dur;setCurTime(Math.floor(simState.current.elapsed));setProgress(p*100);}
  };
  const f=s=>`${Math.floor(s/60)}:${String(Math.floor(s)%60).padStart(2,'0')}`;

  // Missing-audio error state — block Next, don't render fake progress bar
  if (showError) {
    return (
      <div style={{display:'flex',flexDirection:'column',height:'100%',fontFamily:S.font}} data-testid="audio-missing-state">
        <Header title={`Practice Test ${testNum} - ${title}`} onNext={undefined} showNext={false} timeRemaining={null} onHome={onHome}/>
        <div className="exam-body" style={{flex:1,padding:'24px 40px',background:S.white,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center'}}>
          <div style={{maxWidth:'560px',width:'100%',background:'#fef3c7',border:'1px solid #f59e0b',borderRadius:'8px',padding:'28px 32px',display:'flex',gap:'16px',alignItems:'flex-start'}}>
            <AlertTriangle size={28} color="#d97706" style={{flexShrink:0,marginTop:'2px'}}/>
            <div>
              <div style={{fontSize:'17px',fontWeight:'700',color:'#92400e',marginBottom:'8px'}}>Audio not available</div>
              <p style={{fontSize:'14px',color:'#78350f',lineHeight:'1.6',margin:0}} data-testid="audio-missing-message">
                This audio hasn't been uploaded yet. Please contact support at{' '}
                <a href="mailto:support@celpipmax.com" style={{color:'#0b5cad',fontWeight:'600'}}>support@celpipmax.com</a>{' '}
                so we can fix it. You will not be able to continue this section until the audio is available.
              </p>
              <p style={{fontSize:'12px',color:'#92400e',marginTop:'12px',marginBottom:0,fontStyle:'italic'}}>
                Admins: append <code style={{background:'#fde68a',padding:'1px 5px',borderRadius:'3px'}}>?devAudio=1</code> to the URL to bypass for QA.
              </p>
            </div>
          </div>
        </div>
        <Foot onBack={onBack}/>
      </div>
    );
  }

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',fontFamily:S.font}}>
      <Header title={`Practice Test ${testNum} - ${title}`} onNext={done?onNext:undefined} showNext={done} timeRemaining={null} onHome={onHome}/>
      <div className="exam-body" style={{flex:1,padding:'24px 40px',background:S.white,display:'flex',flexDirection:'column',alignItems:'center'}}>
        <div style={{display:'flex',alignItems:'flex-start',marginBottom:'28px',width:'100%',maxWidth:'700px'}}>
          <InfoIcon/>
          <span style={{color:S.blue,fontSize:'15px',lineHeight:'1.6',fontWeight:'600'}}>
            {step.heading || 'Listen to the conversation. You will hear the conversation only once.'}
          </span>
        </div>
        <div className="exam-audio-box" style={{background:'#dee2e6',borderRadius:'6px',padding:'18px 28px',width:'400px',textAlign:'center',marginBottom:'14px'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'14px'}}>
            <Volume2 size={26} color="#555"/>
            <div style={{flex:1}}>
              <div style={{fontSize:'14px',color:'#555',marginBottom:'5px',fontWeight:'500'}}>{playing?'Playing...':done?'Complete':'Paused'}</div>
              <div style={{background:'#fff',height:'8px',borderRadius:'4px',overflow:'hidden'}}>
                <div style={{background:'#17a2b8',height:'100%',width:`${progress}%`,transition:'width 0.15s linear',borderRadius:'4px'}}/>
              </div>
            </div>
          </div>
        </div>
        {hasReal && <audio ref={audioRef} src={step.audio_url} preload="auto" style={{display:'none'}}/>}
        <div className="exam-audio-box" style={{display:'flex',alignItems:'center',gap:'10px',marginBottom:'14px',width:'400px'}}>
          <button onClick={()=>seek(-5)} style={{background:'none',border:'none',cursor:'pointer',padding:'4px'}} title="Rewind 5s" data-testid="audio-rewind-btn"><SkipBack size={18} color="#555"/></button>
          <button onClick={toggle} style={{background:'none',border:'none',cursor:'pointer',padding:'4px'}} data-testid="audio-play-btn">
            {playing ? <Pause size={20} color="#333"/> : <Play size={20} color="#333"/>}
          </button>
          <button onClick={()=>seek(5)} style={{background:'none',border:'none',cursor:'pointer',padding:'4px'}} title="Forward 5s" data-testid="audio-forward-btn"><SkipForward size={18} color="#555"/></button>
          <span style={{fontSize:'13px',color:'#555',minWidth:'72px'}}>{f(curTime)} / {f(dur)}</span>
          <div onClick={clickSeek} style={{flex:1,background:'#ccc',height:'5px',borderRadius:'3px',cursor:'pointer',position:'relative'}}>
            <div style={{background:'#333',height:'100%',width:`${progress}%`,borderRadius:'3px',transition:'width 0.1s linear'}}/>
          </div>
          <Volume2 size={15} color="#888"/>
        </div>
        <div style={{border:'1px solid #555',padding:'7px 18px',fontSize:'13px',color:'#666',fontStyle:'italic',borderRadius:'2px'}}>
          This playbar will not appear in the official test.
        </div>
      </div>
      <Foot onBack={onBack}/>
    </div>
  );
}

/* ─── break_timer ────────────────────────────────────────────── */
function StepBreakTimer({step, title, testNum, onNext, onBack, onHome}) {
  const [t, setT] = useState(step.seconds||10);
  useEffect(()=>{if(t<=0){onNext();return;}const id=setTimeout(()=>setT(v=>v-1),1000);return()=>clearTimeout(id);},[t,onNext]);
  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',fontFamily:S.font}}>
      <Header title={`Practice Test ${testNum} - ${title}`} onNext={onNext} showNext={true} timeRemaining={null} onHome={onHome}/>
      <div className="exam-body" style={{flex:1,padding:'24px 40px',background:S.white,display:'flex',flexDirection:'column',alignItems:'center'}}>
        <div style={{display:'flex',alignItems:'flex-start',marginBottom:'28px',width:'100%',maxWidth:'700px'}}>
          <InfoIcon/><span style={{color:S.blue,fontSize:'15px',lineHeight:'1.6',fontWeight:'600'}}>{step.message||'The next section will begin shortly.'}</span>
        </div>
        <div style={{background:S.lightBg,borderRadius:'8px',padding:'24px 48px',display:'flex',alignItems:'center',gap:'18px',border:'1px solid #ddd'}}>
          <div style={{width:'44px',height:'44px',borderRadius:'50%',background:'#e0e0e0',display:'flex',alignItems:'center',justifyContent:'center'}}>
            <span style={{fontSize:'22px'}}>&#9201;</span>
          </div>
          <div style={{textAlign:'center'}}>
            <div style={{fontSize:'15px',fontWeight:'bold',color:S.textDark}}>Preparation Time</div>
            <div style={{fontSize:'28px',fontWeight:'bold',color:S.blue}}>{t}</div>
          </div>
        </div>
      </div>
      <Foot onBack={onBack}/>
    </div>
  );
}

/* ─── mcq_split (audio left + MCQ right for listening, OR passage left + MCQ right for reading) ─── */
function StepMCQSplit({step, title, testNum, questions, qIdx, responses, onSelect, timeRemaining, onNext, onBack, onHome, section, counterLabel}) {
  const q = questions[qIdx];
  const audioRef = useRef(null);
  const [audioDone, setAudioDone] = useState(false);
  // Listening Media Guardrails (Step 1): block Next until audio.ended fires for real audio.
  // ?devAudio=1 keeps the legacy 12s fake-playback fallback for admin/preview testing.
  const isReading = (section?.skill_id === 'reading');
  const hasAudio = !!((q?.audio_url || '').trim());
  const devAudio = (typeof window !== 'undefined') &&
    new URLSearchParams(window.location.search).get('devAudio') === '1';
  const audioMissing = !isReading && !hasAudio && !devAudio;

  useEffect(() => {
    setAudioDone(false);
    if (isReading) { setAudioDone(true); return; }
    if (hasAudio && audioRef.current) {
      const a = audioRef.current;
      const onEnd = () => setAudioDone(true);
      a.addEventListener('ended', onEnd);
      return () => a.removeEventListener('ended', onEnd);
    }
    if (!hasAudio && devAudio) {
      const t = setTimeout(() => setAudioDone(true), 12000);
      return () => clearTimeout(t);
    }
    // audioMissing: leave audioDone=false → NEXT stays hidden until audio is uploaded
  }, [q?.id, hasAudio, isReading, devAudio]);

  if(!q) return null;
  const sel = responses[q.id]?.selected_option||'';
  const hasImgOpts = (q.options||[]).some(o=>o.image_url);

  // Determine mode: reading shows passage/image on left; listening shows audio player.
  const passageText = q.passage_text || section?.section_passage_text || step?.left_content || '';
  const passageImage = q.image_url || section?.context_image_url || step?.left_image_url || '';
  const leftHeading = isReading
    ? (step?.left_heading || 'Read the following passage.')
    : null;

  const canNext = isReading || audioDone;

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',fontFamily:S.font}}>
      <Header title={`Practice Test ${testNum} - ${title}`} timeRemaining={timeRemaining} onNext={canNext?onNext:undefined} showNext={canNext} onHome={onHome}/>
      <div className="exam-split" style={{flex:1,display:'flex',overflow:'hidden'}}>
        {/* LEFT: reading passage/image OR listening audio */}
        <div className={`exam-pane exam-pane-left${isReading ? ' exam-pane-passage' : ''}`} style={{width:'45%',borderRight:`1px solid ${S.panelBorder}`,padding:'20px',overflowY:'auto',background:S.white}} data-testid="left-panel">
          {isReading ? (
            <>
              <div style={{display:'flex',alignItems:'flex-start',marginBottom:'14px'}}>
                <InfoIcon/><span style={{color:S.blue,fontSize:'14px',fontWeight:'600',lineHeight:'1.5'}}>{leftHeading}</span>
              </div>
              {passageImage && <img src={passageImage} alt="Reading" style={{maxWidth:'100%',maxHeight:'260px',border:'1px solid #ddd',borderRadius:'4px',marginBottom:'14px',display:'block'}} data-testid="reading-image"/>}
              {passageText ? (
                <div style={{fontSize:'14px',color:S.textBody,lineHeight:'1.75',whiteSpace:'pre-wrap'}} data-testid="reading-passage"><RichText value={passageText}/></div>
              ) : !passageImage && (
                <div style={{fontSize:'13px',color:S.textLight,fontStyle:'italic',padding:'12px',background:'#f8f9fa',borderRadius:'4px',border:'1px dashed #ddd'}}>No reading passage provided for this question.</div>
              )}
            </>
          ) : (
            <>
              <div style={{display:'flex',alignItems:'flex-start',marginBottom:'18px'}}>
                <InfoIcon/><span style={{color:S.blue,fontSize:'14px',fontWeight:'600',lineHeight:'1.5'}}>Listen to the question. You will hear it only <strong>once</strong>.</span>
              </div>
              {audioMissing ? (
                /* Step 1 — production guardrail: explicit error state when audio_url is empty */
                <div data-testid="audio-missing-state" style={{maxWidth:'380px',margin:'0 auto',background:'#fef3c7',border:'1px solid #f59e0b',borderRadius:'6px',padding:'18px 20px',display:'flex',gap:'12px',alignItems:'flex-start'}}>
                  <AlertTriangle size={22} color="#d97706" style={{flexShrink:0,marginTop:'2px'}}/>
                  <div>
                    <div style={{fontSize:'14px',fontWeight:'700',color:'#92400e',marginBottom:'6px'}}>Audio not available</div>
                    <p data-testid="audio-missing-message" style={{fontSize:'13px',color:'#78350f',lineHeight:'1.55',margin:0}}>
                      This audio hasn't been uploaded yet. Please contact{' '}
                      <a href="mailto:support@celpipmax.com" style={{color:'#0b5cad',fontWeight:'600'}}>support@celpipmax.com</a>{' '}
                      so we can fix it. You will not be able to continue this section until the audio is available.
                    </p>
                    <p style={{fontSize:'11px',color:'#92400e',marginTop:'8px',marginBottom:0,fontStyle:'italic'}}>
                      Admins: append <code style={{background:'#fde68a',padding:'1px 4px',borderRadius:'3px'}}>?devAudio=1</code> to bypass for QA.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{background:'#dee2e6',borderRadius:'5px',padding:'14px 20px',maxWidth:'340px',margin:'0 auto 12px'}}>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'10px'}}>
                      <Volume2 size={22} color="#666"/>
                      <div style={{flex:1,textAlign:'center'}}>
                        <div style={{fontSize:'13px',color:'#555',marginBottom:'4px'}}>{audioDone?'Complete':'Playing...'}</div>
                        <div style={{background:'#fff',height:'7px',borderRadius:'3px',overflow:'hidden'}}>
                          <div style={{background:'#17a2b8',height:'100%',width:audioDone?'100%':'60%',borderRadius:'3px',transition:'width 0.3s linear'}}/>
                        </div>
                      </div>
                    </div>
                  </div>
                  {hasAudio && <audio ref={audioRef} controls src={q.audio_url} style={{width:'100%',maxWidth:'340px',display:'block',margin:'0 auto'}} autoPlay data-testid="question-audio"/>}
                  <div style={{border:'1px solid #555',padding:'5px 10px',fontSize:'11px',color:'#777',fontStyle:'italic',maxWidth:'340px',margin:'10px auto 0',textAlign:'center',borderRadius:'2px'}}>
                    This playbar will not appear in the official test.
                  </div>
                </>
              )}
            </>
          )}
        </div>
        {/* RIGHT: MCQ — reading variant gets sky-blue tint to match real CELPIP */}
        <div className="exam-pane exam-pane-right" style={{width:'55%',padding:'20px',overflowY:'auto',background:isReading?S.panelTint:S.white}} data-testid="question-panel">
          <div style={{marginBottom:'6px',fontSize:'13px',color:S.textLight}}>{counterLabel || `Question ${qIdx+1} of ${questions.length}`}</div>
          <div style={{display:'flex',alignItems:'flex-start',marginBottom:'14px'}}>
            <InfoIcon/><span style={{color:S.blue,fontWeight:'bold',fontSize:'14px'}}>Choose the <span style={{color:S.blue}}>best</span> answer to each question.</span>
          </div>
          {/* In reading mode, show the question prompt above the options. In listening, the prompt comes from the audio. */}
          {isReading && q.prompt_text && (
            <div style={{fontSize:'14px',color:S.textDark,fontWeight:'600',marginBottom:'14px',lineHeight:'1.6'}} data-testid="reading-prompt"><RichText value={q.prompt_text}/></div>
          )}
          <div style={{display:'flex',flexDirection:'column',gap:hasImgOpts?'8px':'10px'}}>
            {(q.options||[]).map(opt=>{
              const isSel=sel===opt.id;
              return (
                <label key={opt.id} style={{display:'flex',alignItems:opt.image_url?'flex-start':'center',gap:'10px',padding:opt.image_url?'6px':'10px 14px',border:`2px solid ${isSel?S.blue:'#ddd'}`,borderRadius:'4px',cursor:'pointer',background:isSel?'#e3eef9':S.white,transition:'all 0.12s'}} data-testid={`option-${opt.id}`}>
                  <input type="radio" name={`q-${q.id}`} checked={isSel} onChange={()=>onSelect(q.id,opt.id)} style={{width:'18px',height:'18px',accentColor:S.blue,marginTop:opt.image_url?'4px':0,flexShrink:0}}/>
                  {opt.image_url ? <img src={opt.image_url} alt={opt.text||opt.id} style={{maxWidth:'180px',maxHeight:'120px',borderRadius:'3px',border:'1px solid #ddd'}}/> : <span style={{fontSize:'14px',color:S.textBody}}>{opt.text}</span>}
                </label>
              );
            })}
          </div>
        </div>
      </div>
      <Foot onBack={onBack} showAnswerKey={true} answerKeyQuestions={questions} responses={responses}/>
    </div>
  );
}

/* ─── dropdown_split (passage/diagram left + dropdowns right) ── */
function StepDropdownSplit({step, title, testNum, questions, responses, onSelect, timeRemaining, onNext, onBack, onHome, section}) {
  const leftContent = step.left_content || section?.section_passage_text || '';
  const leftImage = step.left_image_url || '';
  const rightHeading = step.right_heading || '';
  const rightSubHeading = step.right_sub_heading || '';

  const ddStyle = {padding:'4px 22px 4px 8px',border:'1px solid #b8bec6',borderRadius:'2px',fontSize:'13px',minWidth:'108px',fontFamily:S.font,background:'#dde6ee',color:'#1f2937',cursor:'pointer',appearance:'auto'};
  // Whenever we have ≥1 dropdown_paragraph question we render each question
  // in its OWN framed container (matches real CELPIP R1/R4 — instruction
  // header OUTSIDE the box, statements/paragraph INSIDE). Simple dropdown
  // questions stay grouped together inside a single shared container.
  const hasParagraphQ = questions.some(q => q.question_type === 'dropdown_paragraph');

  const renderParagraphQ = (q, idx) => {
    const picks = responses[q.id]?.selected_options || {};
    const setPick = (sid, optId) => onSelect(q.id, {selected_options: {...picks, [sid]: optId}, _kind: 'dropdown_paragraph'});
    const headerText = q.prompt_text && q.prompt_text.trim()
      ? q.prompt_text
      : 'Choose the best way to complete each statement from the drop-down menu (▼).';
    return (
      <div key={q.id} style={{marginBottom:'18px'}} data-testid={`dropdown-paragraph-q-${idx}`}>
        <div style={{display:'flex',alignItems:'flex-start',marginBottom:'10px',gap:'4px'}}>
          <InfoIcon/><span style={{color:S.blue,fontWeight:'bold',fontSize:'14px'}}><RichText value={headerText}/></span>
        </div>
        <div style={{border:`1px solid ${S.qBoxBorder}`,borderRadius:S.qBoxRadius,padding:'14px 18px',background:S.qBoxBg}}>
          <DropdownParagraphBody question={q} picks={picks} setPick={setPick} ddStyle={ddStyle} testIdPrefix={`dropdown-paragraph-${idx}`}/>
        </div>
      </div>
    );
  };

  const renderSimpleQ = (q, idx) => {
    const parts = (q.prompt_text || '').split(/___|\{blank\}/);
    const sel = responses[q.id]?.selected_option || '';
    const dd = (
      <span style={DD_WRAP}>
        <select value={sel} onChange={e=>onSelect(q.id,e.target.value)} style={ddStyle} data-testid={`dropdown-select-${idx}`}>
          <option value=""></option>
          {(q.options||[]).map(o=><option key={o.id} value={o.id}>{o.text}</option>)}
        </select>
      </span>
    );

    if (parts.length > 1) {
      return <div key={q.id} style={{fontSize:'14px',color:S.textBody,lineHeight:'2.4',padding:'10px 0',borderBottom:'1px solid #e8eaed'}} data-testid={`dropdown-q-${idx}`}>
        {parts.map((p,pi)=><React.Fragment key={pi}><span>{p}</span>{pi<parts.length-1&&dd}</React.Fragment>)}
      </div>;
    }
    return <div key={q.id} style={{display:'flex',alignItems:'flex-start',flexWrap:'wrap',gap:'6px',fontSize:'14px',color:S.textBody,lineHeight:'2.4',padding:'12px 0',borderBottom:'1px solid #e8eaed'}} data-testid={`dropdown-q-${idx}`}>
      {dd}<span style={{flex:1}}>- {idx+1}. <RichText value={q.prompt_text}/></span>
    </div>;
  };

  const renderDropdownQ = (q, idx) =>
    q.question_type === 'dropdown_paragraph' ? renderParagraphQ(q, idx) : renderSimpleQ(q, idx);

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',fontFamily:S.font}}>
      <Header title={`Practice Test ${testNum} - ${title}`} timeRemaining={timeRemaining} onNext={onNext} onHome={onHome}/>
      <div className="exam-split" style={{flex:1,display:'flex',overflow:'hidden',background:'#f5f6f8'}}>
        {/* LEFT - passage */}
        <div className="exam-pane exam-pane-left exam-pane-passage" style={{width:'48%',borderRight:`1px solid ${S.panelBorder}`,padding:'20px 24px',overflowY:'auto',background:S.white}} data-testid="left-panel">
          {step.left_heading && <div style={{display:'flex',alignItems:'flex-start',marginBottom:'14px'}}><InfoIcon/><span style={{color:S.blue,fontWeight:'bold',fontSize:'15px'}}>{step.left_heading}</span></div>}
          {leftImage && <div style={{marginBottom:'14px'}}><img src={leftImage} alt="Diagram" style={{maxWidth:'100%',border:'1px solid #ddd',borderRadius:'4px'}}/></div>}
          {leftContent && <div style={{fontSize:'14px',color:S.textBody,lineHeight:'1.75',whiteSpace:'pre-wrap'}}><RichText value={leftContent}/></div>}
        </div>
        {/* RIGHT - questions (sky-blue tint to match real CELPIP) */}
        <div className="exam-pane exam-pane-right" style={{width:'52%',padding:'20px 24px',overflowY:'auto',background:S.panelTint}} data-testid="question-panel">
          {rightHeading && <div style={{display:'flex',alignItems:'flex-start',marginBottom:'16px'}}><InfoIcon/><span style={{color:S.blue,fontWeight:'bold',fontSize:'15px'}}>{rightHeading}</span></div>}
          {rightSubHeading && <div style={{display:'flex',alignItems:'flex-start',marginBottom:'14px',padding:'10px 14px',background:'#f0f4f8',borderRadius:'4px',border:'1px solid #d8dde3'}}><InfoIcon/><span style={{color:S.blue,fontWeight:'bold',fontSize:'13px'}}>{rightSubHeading}</span></div>}
          {hasParagraphQ ? (
            // Each dropdown_paragraph question renders its own framed box +
            // header — matches real CELPIP R1/R4 (separate framed blocks per
            // instruction). Don't wrap them in an extra outer container.
            <div>{questions.map((q,i)=>renderDropdownQ(q,i))}</div>
          ) : (
            // Simple dropdowns share one framed container.
            <div style={{border:`1px solid ${S.qBoxBorder}`,borderRadius:S.qBoxRadius,padding:'14px 18px',background:S.qBoxBg}}>
              {questions.map((q,i)=>renderDropdownQ(q,i))}
            </div>
          )}
        </div>
      </div>
      <Foot onBack={onBack} showAnswerKey={true} answerKeyQuestions={questions} responses={responses}/>
    </div>
  );
}

/* ─── dropdown_paragraph (ONE question with multiple statements sharing an option pool) ─── */
function StepDropdownParagraph({step, title, testNum, question, response, onSelect, timeRemaining, onNext, onBack, onHome}) {
  const q = question || {};
  const statements = q.statements || [];
  const options = q.options || [];
  const picks = (response && response.selected_options) || {};
  const ddStyle = {padding:'4px 22px 4px 8px',border:'1px solid #b8bec6',borderRadius:'2px',fontSize:'13px',minWidth:'108px',fontFamily:S.font,background:'#dde6ee',color:'#1f2937',cursor:'pointer',appearance:'auto'};
  const setPick = (sid, optId) => {
    const next = {...picks, [sid]: optId};
    onSelect(q.id, next);
  };
  const headerText = q.prompt_text && q.prompt_text.trim()
    ? q.prompt_text
    : 'Choose the best way to complete each statement from the drop-down menu (▼).';

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',fontFamily:S.font}}>
      <Header title={`Practice Test ${testNum} - ${title}`} timeRemaining={timeRemaining} onNext={onNext} onHome={onHome}/>
      <div className="exam-body" style={{flex:1,padding:'24px 40px',background:S.panelTint,overflowY:'auto'}} data-testid="dropdown-paragraph-container">
        <div style={{display:'flex',alignItems:'flex-start',marginBottom:'14px'}}>
          <InfoIcon/><span style={{color:S.blue,fontWeight:'bold',fontSize:'14px'}}><RichText value={headerText}/></span>
        </div>
        <div style={{maxWidth:'820px',border:`1px solid ${S.qBoxBorder}`,borderRadius:S.qBoxRadius,padding:'14px 18px',background:S.qBoxBg}}>
          <DropdownParagraphBody question={q} picks={picks} setPick={setPick} ddStyle={ddStyle} testIdPrefix="dropdown-paragraph"/>
        </div>
      </div>
      <Foot onBack={onBack} showAnswerKey={true} answerKeyQuestions={[q]} responses={{[q.id]: response}}/>
    </div>
  );
}

/* Renders a text string honoring embedded line breaks (`\n`). Newlines authored
   in the admin statement editor become real <br/> breaks in the exam, so a
   Reading Part 1 "response message" reads as multiple lines/paragraphs instead
   of one unbroken block. */
function renderTextWithBreaks(text) {
  const lines = String(text || '').split('\n');
  return lines.map((line, li) => (
    <React.Fragment key={li}>
      {li > 0 && <br/>}
      {line}
    </React.Fragment>
  ));
}

/* Shared body renderer for dropdown_paragraph — used by both the standalone
   StepDropdownParagraph and inline question_group dispatch. Honors
   `q.paragraph_layout`: when true, statements join into one flowing paragraph
   with inline dropdowns (matches real CELPIP R1 "response message" layout).
   When false, renders each statement as its own numbered line. */
function DropdownParagraphBody({question, picks, setPick, ddStyle, testIdPrefix}) {
  const q = question || {};
  const statements = q.statements || [];
  const options = q.options || [];
  const flowing = !!q.paragraph_layout;

  const buildDropdown = (s, idx) => {
    const stmtOpts = (Array.isArray(s.option_ids) && s.option_ids.length > 0)
      ? options.filter(o => s.option_ids.includes(o.id))
      : options;
    const sel = picks[s.id] || '';
    return (
      <span key={`dd-${s.id}`} style={DD_WRAP}>
        <select value={sel} onChange={e=>setPick(s.id, e.target.value)} style={ddStyle} data-testid={`${testIdPrefix}-select-${idx}`}>
          <option value=""></option>
          {stmtOpts.map(o => <option key={o.id} value={o.id}>{o.text}</option>)}
        </select>
      </span>
    );
  };

  if (flowing) {
    // Flowing paragraph — join all statements with a single space, drop the
    // numbering, no per-line border. Each statement's `___` becomes its inline
    // dropdown. Statements without `___` get the dropdown appended at the end.
    return (
      <div style={{maxWidth:'820px',fontSize:'14px',color:S.textBody,lineHeight:'2.4'}} data-testid={`${testIdPrefix}-flowing`}>
        <p style={{margin:0}}>
          {statements.map((s, i) => {
            const parts = (s.text || '').split(/___|\{blank\}/);
            const dd = buildDropdown(s, i);
            const trailingSpace = i < statements.length - 1 ? ' ' : '';
            if (parts.length > 1) {
              return (
                <React.Fragment key={s.id || i}>
                  {parts.map((p, pi) => (
                    <React.Fragment key={pi}>
                      <span>{renderTextWithBreaks(p)}</span>
                      {pi < parts.length - 1 && dd}
                    </React.Fragment>
                  ))}
                  {trailingSpace}
                </React.Fragment>
              );
            }
            return <React.Fragment key={s.id || i}><span>{renderTextWithBreaks(s.text)}</span>{dd}{trailingSpace}</React.Fragment>;
          })}
        </p>
      </div>
    );
  }

  // Default — numbered list, one statement per line. Wider line spacing
  // matches the real CELPIP R1/R4 look.
  return (
    <div style={{maxWidth:'820px'}}>
      {statements.map((s, i) => {
        const parts = (s.text || '').split(/___|\{blank\}/);
        const dd = buildDropdown(s, i);
        const hasBlank = parts.length > 1;
        const isLast = i === statements.length - 1;
        return (
          <div key={s.id || i} style={{fontSize:'14px',color:S.textBody,lineHeight:'2.4',padding:'12px 0',borderBottom: isLast ? 'none' : '1px solid #e8eaed'}} data-testid={`${testIdPrefix}-statement-${i}`}>
            <span style={{marginRight:'6px'}}>{i+1}.</span>
            {hasBlank
              ? parts.map((p, pi) => (
                  <React.Fragment key={pi}>
                    <span>{renderTextWithBreaks(p)}</span>
                    {pi < parts.length - 1 && dd}
                  </React.Fragment>
                ))
              : <><span>{renderTextWithBreaks(s.text)}</span>{dd}</>
            }
          </div>
        );
      })}
    </div>
  );
}

/* ─── dropdown_wide (full-width) ─────────────────────────────── */
function StepDropdownWide({step, title, testNum, questions, responses, onSelect, timeRemaining, onNext, onBack, onHome}) {
  const ddStyle = {padding:'4px 22px 4px 8px',border:'1px solid #b8bec6',borderRadius:'2px',fontSize:'13px',minWidth:'108px',fontFamily:S.font,background:'#dde6ee',color:'#1f2937',cursor:'pointer',appearance:'auto'};
  const hasParagraphQ = questions.some(q => q.question_type === 'dropdown_paragraph');

  const renderQ = (q,idx) => {
    if (q.question_type === 'dropdown_paragraph') {
      const picks = responses[q.id]?.selected_options || {};
      const setPick = (sid, optId) => onSelect(q.id, {selected_options: {...picks, [sid]: optId}, _kind: 'dropdown_paragraph'});
      const headerText = q.prompt_text && q.prompt_text.trim()
        ? q.prompt_text
        : 'Choose the best way to complete each statement from the drop-down menu (▼).';
      return (
        <div key={q.id} style={{marginBottom:'18px'}} data-testid={`dropdown-paragraph-q-${idx}`}>
          <div style={{display:'flex',alignItems:'flex-start',marginBottom:'10px',gap:'4px'}}>
            <InfoIcon/>
            <span style={{color:S.blue,fontWeight:'bold',fontSize:'14px'}}><RichText value={headerText}/></span>
          </div>
          <div style={{border:`1px solid ${S.qBoxBorder}`,borderRadius:S.qBoxRadius,padding:'14px 18px',background:S.qBoxBg}}>
            <DropdownParagraphBody question={q} picks={picks} setPick={setPick} ddStyle={ddStyle} testIdPrefix={`dropdown-paragraph-${idx}`}/>
          </div>
        </div>
      );
    }
    const parts = (q.prompt_text || '').split(/___|\{blank\}/);
    const sel = responses[q.id]?.selected_option||'';
    const dd = (
      <span style={DD_WRAP}>
        <select value={sel} onChange={e=>onSelect(q.id,e.target.value)} style={ddStyle} data-testid={`dropdown-select-${idx}`}>
          <option value=""></option>
          {(q.options||[]).map(o=><option key={o.id} value={o.id}>{o.text}</option>)}
        </select>
      </span>
    );
    if(parts.length>1) return <div key={q.id} style={{fontSize:'14px',color:S.textBody,lineHeight:'2.4',padding:'10px 0',borderBottom:'1px solid #e8eaed'}} data-testid={`dropdown-q-${idx}`}>{parts.map((p,pi)=><React.Fragment key={pi}><span>{p}</span>{pi<parts.length-1&&dd}</React.Fragment>)}</div>;
    return <div key={q.id} style={{display:'flex',alignItems:'flex-start',flexWrap:'wrap',gap:'6px',fontSize:'14px',color:S.textBody,lineHeight:'2.4',padding:'12px 0',borderBottom:'1px solid #e8eaed'}} data-testid={`dropdown-q-${idx}`}>{dd}<span style={{flex:1}}>- {idx+1}. <RichText value={q.prompt_text}/></span></div>;
  };
  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',fontFamily:S.font}}>
      <Header title={`Practice Test ${testNum} - ${title}`} timeRemaining={timeRemaining} onNext={onNext} onHome={onHome}/>
      <div className="exam-body" style={{flex:1,padding:'24px 40px',background:S.panelTint,overflowY:'auto'}}>
        {!hasParagraphQ && (
          <div style={{display:'flex',alignItems:'flex-start',marginBottom:'20px'}}>
            <InfoIcon/><span style={{color:S.blue,fontWeight:'bold',fontSize:'14px'}}>Choose the <span style={{color:S.blue}}>best</span> way to complete each statement from the drop-down menu (<span style={{fontSize:'10px'}}>&#9660;</span>).</span>
          </div>
        )}
        {hasParagraphQ ? (
          <div style={{maxWidth:'820px'}}>{questions.map((q,i)=>renderQ(q,i))}</div>
        ) : (
          <div style={{maxWidth:'820px',border:`1px solid ${S.qBoxBorder}`,borderRadius:S.qBoxRadius,padding:'14px 18px',background:S.qBoxBg}}>{questions.map((q,i)=>renderQ(q,i))}</div>
        )}
      </div>
      <Foot onBack={onBack} showAnswerKey={true} answerKeyQuestions={questions} responses={responses}/>
    </div>
  );
}

/* ─── writing_task — matches CELPIP exactly ──────────────────── */
/* ─── writing_task (Email / Survey) ────────────────────────────
   Supports both:
   • Writing Task 1 (Email) — prompt + bulleted instructions on the left,
     textarea on the right. No options.
   • Writing Task 2 (Survey) — authentic CELPIP two-screen flow:
       Screen 1: Left = passage_text (survey background).
                 Right = prompt + radio options A / B. Textarea HIDDEN.
                 NEXT disabled until the student picks an option.
       Screen 2: Left = same passage. Right = selected option highlighted +
                 "Explain the reasons for your choice" heading + textarea.
   Response shape written back: {text_response, selected_option, selected_label}.
*/
function StepWritingTask({step, title, testNum, question, value, onResponse, timeRemaining, onNext, onBack, onHome}) {
  // value is an object: {text_response, selected_option, selected_label}
  const v = (typeof value === 'object' && value !== null) ? value : { text_response: value || '' };
  const text = v.text_response || '';
  const selectedOpt = v.selected_option || '';
  const wc = text.trim() ? text.trim().split(/\s+/).length : 0;
  // Survey-only options. W1 (writing_email) is a pure textarea task —
  // never show the option-picker even if a question has stale `options`
  // left over from a prior MCQ-typed question being switched to W1.
  const isSurvey = question?.question_type === 'writing_survey';
  const hasOptions = isSurvey && (question?.options || []).length > 0;
  const [showSamples, setShowSamples] = useState(false);
  const [sampleTab, setSampleTab] = useState(0);

  // Two-phase UI for survey tasks — pre-select vs. post-select (textarea visible)
  const isPostSelection = isSurvey && !!selectedOpt;

  const promptText = question?.prompt_text || '';
  const passageText = question?.passage_text || '';
  const promptIsHtml = /<\/?(p|br|ul|ol|li|strong|b|em|i|u|div|span|h[1-6])\b/i.test(promptText);
  const promptLines = promptText.split('\n');
  const situationLines = promptLines.filter(l => !l.trim().startsWith('-'));
  const instructionBullets = promptLines.filter(l => l.trim().startsWith('-')).map(l => l.trim().replace(/^-\s*/, ''));
  const surveyTitle = step.left_sub_heading || '';
  const hasSampleResponses = question?.show_sample_responses && (question?.sample_responses || []).length > 0;
  const samples = question?.sample_responses || [];

  // For surveys, Left panel uses passage_text if authored; otherwise falls back
  // to the prompt (legacy questions that stuffed everything into prompt_text).
  const leftBody = (isSurvey && passageText) ? passageText : (promptIsHtml ? '' : situationLines.join('\n'));

  // Gate the header's Next button: survey tasks disable Next until an option is picked.
  const canProceed = !isSurvey || !!selectedOpt;

  const setOption = (opt) => {
    const label = opt.label || opt.text || '';
    onResponse?.({ selected_option: opt.id, selected_label: label });
  };
  const setText = (t) => onResponse?.({ text_response: t });

  return (
    <div style={{display: 'flex', flexDirection: 'column', height: '100%', fontFamily: S.font}}>
      <Header title={`Practice Test ${testNum} - ${title}`}
        timeRemaining={timeRemaining}
        onNext={canProceed ? onNext : undefined}
        showNext={canProceed}
        onHome={onHome}
      />
      <div className="exam-split" style={{flex: 1, display: 'flex', overflow: 'hidden'}}>
        {/* LEFT panel — passage / info */}
        <div className="exam-pane exam-pane-left" style={{width: '40%', borderRight: `1px solid ${S.panelBorder}`, padding: '20px 22px', overflowY: 'auto', background: '#f5f7fa'}} data-testid="writing-left-panel">
          <div style={{display: 'flex', alignItems: 'flex-start', marginBottom: '14px'}}>
            <InfoIcon/>
            <span style={{color: S.blue, fontWeight: 'bold', fontSize: '15px'}}>
              {step.left_heading || 'Read the following information.'}
            </span>
          </div>
          {surveyTitle && <div style={{fontWeight: 'bold', fontSize: '14px', color: S.textDark, marginBottom: '10px'}}>{surveyTitle}</div>}
          {(isSurvey && passageText) ? (
            <div style={{fontSize: '14px', color: S.textBody, lineHeight: '1.7', whiteSpace: 'pre-wrap'}}>{passageText}</div>
          ) : promptIsHtml ? (
            <div style={{fontSize: '14px', color: S.textBody, lineHeight: '1.7'}}><RichText value={promptText}/></div>
          ) : (
            <div style={{fontSize: '14px', color: S.textBody, lineHeight: '1.7', whiteSpace: 'pre-wrap'}}>{leftBody}</div>
          )}
          {hasSampleResponses && (
            <div onClick={() => setShowSamples(true)}
              style={{marginTop: '28px', border: '2px solid #999', padding: '16px 22px', textAlign: 'center', cursor: 'pointer', fontSize: '15px', color: S.textDark, fontWeight: '500', lineHeight: '1.4', transition: 'background 0.15s'}}
              onMouseEnter={e => e.currentTarget.style.background = '#eceff3'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              Click here to view sample<br/>responses.
            </div>
          )}
        </div>
        {/* RIGHT panel — prompt + options / textarea */}
        <div className="exam-pane exam-pane-right" style={{width: '60%', padding: '24px 28px', overflowY: 'auto', background: '#fff', display: 'flex', flexDirection: 'column'}} data-testid="writing-right-panel">
          <div style={{display: 'flex', alignItems: 'flex-start', marginBottom: '14px'}}>
            <InfoIcon/>
            <span style={{color: S.blue, fontWeight: 'bold', fontSize: '15px', lineHeight: '1.5'}}>
              {step.right_heading || (hasOptions
                ? 'Choose the option that you prefer. Why do you prefer your choice? Explain the reasons for your choice. Write about 150-200 words.'
                : 'Write an email in about 150-200 words. Your email should do the following things:')}
            </span>
          </div>

          {/* Email (W1): bullet-list instructions */}
          {!hasOptions && !promptIsHtml && instructionBullets.length > 0 && (
            <ul style={{listStyle: 'disc', paddingLeft: '42px', marginBottom: '18px', color: S.textBody, fontSize: '14px', lineHeight: '1.8'}}>
              {instructionBullets.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          )}

          {/* Survey (W2): radio option cards */}
          {hasOptions && (
            <div style={{marginBottom: '18px', paddingLeft: '4px'}} data-testid="writing-options-list">
              {question.options.map(opt => {
                const isSelected = selectedOpt === opt.id;
                const label = opt.label || opt.text || '';
                const desc = opt.description || '';
                return (
                  <label key={opt.id}
                    data-testid={`writing-option-${opt.id}`}
                    onClick={() => setOption(opt)}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: '14px',
                      padding: '14px 16px', marginBottom: '10px', cursor: 'pointer',
                      border: isSelected ? `2px solid ${S.blue}` : '1px solid #cfd6e0',
                      borderRadius: '6px',
                      background: isSelected ? '#eef4ff' : '#fafbfd',
                      transition: 'background 0.15s, border-color 0.15s',
                    }}>
                    <input
                      type="radio"
                      name={`writing-opt-${question.id}`}
                      checked={isSelected}
                      readOnly
                      style={{width: '18px', height: '18px', accentColor: S.blue, marginTop: '2px', flexShrink: 0, cursor: 'pointer'}}
                    />
                    <div style={{flex: 1}}>
                      <div style={{fontSize: '14px', color: S.textDark, marginBottom: desc ? '4px' : 0}}>
                        <strong>Option {opt.id}: {label}</strong>
                      </div>
                      {desc && <div style={{fontSize: '13px', color: S.textBody, lineHeight: '1.55'}}>{desc}</div>}
                    </div>
                  </label>
                );
              })}
              {!selectedOpt && (
                <div style={{marginTop: '6px', fontSize: '12px', color: '#b45309', fontStyle: 'italic'}}>
                  Select an option to continue. The response area will appear below.
                </div>
              )}
            </div>
          )}

          {/* Post-selection heading */}
          {isPostSelection && (
            <div style={{display: 'flex', alignItems: 'flex-start', marginBottom: '12px'}}>
              <InfoIcon/>
              <span style={{color: S.blue, fontWeight: 'bold', fontSize: '14px'}}>
                Explain the reasons for your choice. Write about 150-200 words.
              </span>
            </div>
          )}

          {/* Textarea: visible for email always, or for survey after an option is picked */}
          {(!hasOptions || isPostSelection) && (
            <>
              <textarea value={text} onChange={e => setText(e.target.value)} spellCheck={false}
                style={{width: '100%', padding: '14px', border: '1px solid #b8c6d8', resize: 'vertical', fontSize: '14px', lineHeight: '1.6', fontFamily: S.font, outline: 'none', minHeight: '280px', borderRadius: '3px', background: '#fff'}}
                placeholder="" data-testid="writing-textarea"/>
              <div style={{marginTop: '8px', fontSize: '13px', color: '#555', fontWeight: 'bold'}}>
                * NOTE: This practice test is recording your response for AI scoring.
              </div>
              <div style={{marginTop: '4px', fontSize: '12px', display: 'flex', justifyContent: 'space-between'}}>
                <span style={{color: wc < 150 ? '#cc6600' : wc > 200 ? '#cc0000' : '#27ae60'}}>Word Count: {wc}</span>
                <span style={{color: '#888'}}>Target: 150-200 words</span>
              </div>
            </>
          )}
        </div>
      </div>
      <Foot onBack={onBack}/>

      {/* Sample Responses Modal */}
      {showSamples && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:60}} onClick={()=>setShowSamples(false)}>
          <div style={{background:'#fff',borderRadius:'8px',width:'90%',maxWidth:'860px',maxHeight:'85vh',display:'flex',flexDirection:'column',boxShadow:'0 8px 40px rgba(0,0,0,0.25)'}} onClick={e=>e.stopPropagation()}>
            {/* Modal Header */}
            <div style={{padding:'14px 20px',background:S.hdrBg,borderBottom:`1px solid ${S.hdrBorder}`,borderRadius:'8px 8px 0 0'}}>
              <span style={{fontWeight:'bold',fontSize:'15px',color:S.hdrText}}>{title} (Sample Responses)</span>
            </div>
            {/* Prompt recap */}
            <div style={{padding:'16px 24px',borderBottom:'1px solid #e0e0e0',background:'#f8f9fb'}}>
              <div style={{display:'flex',alignItems:'flex-start',marginBottom:'10px'}}><InfoIcon/><span style={{color:S.blue,fontWeight:'bold',fontSize:'14px'}}>Read the following information.</span></div>
              <p style={{fontSize:'13px',color:S.textBody,lineHeight:'1.6'}}>{situationLines.join(' ').slice(0,200)}...</p>
              {instructionBullets.length > 0 && (
                <>
                  <div style={{display:'flex',alignItems:'flex-start',marginTop:'12px',marginBottom:'6px'}}><InfoIcon/><span style={{color:S.blue,fontWeight:'bold',fontSize:'13px'}}>Your email should do the following things:</span></div>
                  <ul style={{listStyle:'disc',paddingLeft:'36px',color:S.textBody,fontSize:'13px',lineHeight:'1.6'}}>
                    {instructionBullets.map((b,i)=><li key={i}>{b}</li>)}
                  </ul>
                </>
              )}
            </div>
            {/* Level Tabs */}
            <div style={{display:'flex',justifyContent:'center',padding:'16px 24px 0',gap:'0'}}>
              {samples.map((sr,i)=>(
                <button key={i} onClick={()=>setSampleTab(i)} style={{
                  padding:'10px 24px',fontSize:'14px',fontWeight:sampleTab===i?'bold':'500',
                  background:sampleTab===i?'#1a1f2e':'#fff',color:sampleTab===i?'#fff':S.textBody,
                  border:`1px solid ${sampleTab===i?'#1a1f2e':'#ccc'}`,cursor:'pointer',fontFamily:S.font,
                  borderRadius:i===0?'4px 0 0 4px':i===samples.length-1?'0 4px 4px 0':'0',
                }}>{sr.level || `Level ${i+1}`}</button>
              ))}
            </div>
            {/* Response Content */}
            <div style={{flex:1,overflowY:'auto',padding:'20px 24px'}}>
              <div style={{border:'1px solid #ddd',borderRadius:'4px',padding:'20px 24px',background:'#fff',fontSize:'14px',color:S.textBody,lineHeight:'1.8',whiteSpace:'pre-wrap'}}>
                {samples[sampleTab]?.text || 'No sample response available.'}
              </div>
            </div>
            {/* Footer */}
            <div style={{padding:'12px 24px',borderTop:'1px solid #e0e0e0',background:'#f8f9fb',borderRadius:'0 0 8px 8px'}}>
              <button onClick={()=>setShowSamples(false)} style={{padding:'8px 20px',background:'#e8e8e8',border:'1px solid #bbb',fontSize:'13px',cursor:'pointer',fontFamily:S.font,borderRadius:'3px',fontWeight:'500'}}>Return to Task</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── speaking_task — full recording + upload + STT ──────────────
   Phase flow (strict):
     prep       → countdown before recording starts
     recording  → mic live, capturing chunks
     uploading  → blob assembled, POST /api/speaking/transcribe in flight
     done       → upload succeeded; audio_url persisted; onComplete fired
     upload_failed → upload error; Retry button shown; NEXT disabled
     mic_denied → getUserMedia denied/failed; Skip button shown
   We ONLY call onComplete once we have a server-backed audio_url so the
   submit_exam payload carries the persistent URL (see Phase 1 brief).
*/
function StepSpeakingTask({step, title, testNum, question, timeRemaining, onNext, onBack, onComplete, onHome}) {
  const [phase, setPhase] = useState('prep');
  const [pt, setPt] = useState(question?.prep_time_seconds || 30);
  const [rt, setRt] = useState(question?.response_time_seconds || 60);
  const [localAudioURL, setLocalAudioURL] = useState(null); // in-browser blob: URL (preview only)
  const [serverAudioURL, setServerAudioURL] = useState(null); // persisted URL from backend
  const [audioBlob, setAudioBlob] = useState(null);
  const [transcript, setTranscript] = useState('');
  const [aiScore, setAiScore] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [micRetryCount, setMicRetryCount] = useState(0);
  const mr = useRef(null);
  const lastBlob = useRef(null); // persisted for retry across re-renders
  const streamRef = useRef(null); // mic stream — acquired EARLY (on the recent
  // user gesture from NEXT) and held through prep + recording. Without this
  // Safari/iOS silently rejects getUserMedia called from the prep-timer
  // callback because the user-gesture token has expired.

  // Eagerly request mic on mount — the NEXT click that landed the student
  // here is the most recent user gesture; calling getUserMedia synchronously
  // (no setTimeout in between) keeps that gesture valid in Safari/iOS too.
  // If the browser is strict and rejects, we surface the mic_denied phase
  // with a "Tap to enable microphone" button that re-tries on a fresh click.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (cancelled) { s.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = s;
      } catch (e) {
        console.warn('Eager getUserMedia failed; will prompt on retry:', e?.name || e);
        if (!cancelled) setPhase('mic_denied');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (phase === 'prep' && pt > 0) { const t = setTimeout(() => setPt(p => p - 1), 1000); return () => clearTimeout(t); }
    else if (phase === 'prep' && pt <= 0) { startRec(); }
    // eslint-disable-next-line
  }, [phase, pt]);
  useEffect(() => {
    if (phase === 'recording' && rt > 0) { const t = setTimeout(() => setRt(r => r - 1), 1000); return () => clearTimeout(t); }
    else if (phase === 'recording' && rt <= 0) { stopRec(); }
    // eslint-disable-next-line
  }, [phase, rt]);

  // Cleanup on unmount — stop mic + free tracks + revoke blob URL to prevent leaks.
  useEffect(() => () => {
    try {
      if (mr.current && mr.current.state === 'recording') mr.current.stop();
      mr.current?.stream?.getTracks()?.forEach(t => t.stop());
      streamRef.current?.getTracks()?.forEach(t => t.stop());
    } catch {}
    try { if (localAudioURL) URL.revokeObjectURL(localAudioURL); } catch {}
    // eslint-disable-next-line
  }, []);

  const uploadBlob = async (blob) => {
    setPhase('uploading');
    setUploadError('');
    try {
      const fd = new FormData();
      fd.append('file', blob, fileNameFor(blob.type || ''));
      fd.append('question_id', question?.id || '');
      const res = await postAudio('/api/speaking/transcribe', fd);
      const { transcript: tx = '', ai_score = null, audio_url = '' } = res.data || {};
      setTranscript(tx);
      setAiScore(ai_score);
      setServerAudioURL(audio_url);
      setPhase('done');
      // Only NOW mark the question as complete — with the persistent URL.
      onComplete?.({ transcript: tx, ai_score, audio_url });
    } catch (err) {
      console.error('Speaking upload failed:', err);
      setUploadError(uploadErrorMessage(err));
      setPhase('upload_failed');
    }
  };

  const startRec = async () => {
    try {
      // Reuse the stream acquired on mount (still attached to a recent user
      // gesture) when possible. Fall back to a fresh request if the eager
      // acquisition was lost (e.g. user revoked, tab background-throttled).
      let s = streamRef.current;
      if (!s || s.getTracks().every(t => t.readyState !== 'live')) {
        s = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = s;
      }
      mr.current = startRecorder(s, async (blob) => {
        s.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        if (isEmptyRecording(blob)) { setPhase('no_audio'); return; }
        lastBlob.current = blob;
        setAudioBlob(blob);
        setLocalAudioURL(URL.createObjectURL(blob));
        // Kick upload — will advance phase to uploading → done OR upload_failed
        await uploadBlob(blob);
      });
      setPhase('recording');
    } catch (e) {
      console.error('getUserMedia failed:', e);
      setPhase('mic_denied');
    }
  };

  const stopRec = () => { if (mr.current?.state === 'recording') mr.current.stop(); };

  const retryUpload = () => {
    if (lastBlob.current) uploadBlob(lastBlob.current);
  };

  // Empty take (muted mic): a fresh click = fresh user gesture → grab the mic now, short prep, record again.
  const recordAgain = async () => {
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      setRt(question?.response_time_seconds || 60);
      setPt(5);
      setPhase('prep');
    } catch (e) {
      console.error('record-again getUserMedia failed:', e);
      setPhase('mic_denied');
    }
  };

  const skipBecauseMicDenied = () => {
    // Student cannot record — mark this question as intentionally skipped with
    // an empty response. Keeps the exam advancing but scores this item as blank.
    onComplete?.({ transcript: '(Microphone not available)', audio_url: '' });
    setPhase('done');
  };

  // Re-attempt mic acquisition on a fresh user gesture (the click that fires
  // this handler counts as a new gesture in every browser, including Safari).
  // Then resume the prep phase so the student can finish the question.
  const retryMicGesture = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = s;
      setMicRetryCount(c => c + 1);
      // Re-enter the prep phase from the top so the student gets a fresh
      // count-down before recording auto-starts.
      setPt(question?.prep_time_seconds || 30);
      setPhase('prep');
    } catch (e) {
      console.error('retry getUserMedia failed:', e);
      setMicRetryCount(c => c + 1);
      // Stay on mic_denied; the helper text below will explain next steps.
    }
  };

  const prepSec = question?.prep_time_seconds || 30;
  const recSec = question?.response_time_seconds || 60;
  const playbackURL = serverAudioURL ? resolveMediaUrl(serverAudioURL) : localAudioURL; // prefer persistent URL

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',fontFamily:S.font}}>
      <Header title={`Practice Test ${testNum} - ${title}`} timeRemaining={timeRemaining}
        onNext={phase==='done' ? onNext : undefined}
        showNext={phase==='done'}
        onHome={onHome}
        rightExtra={<span style={{fontSize:'12px',color:S.hdrText}}>Preparation: <strong>{prepSec}s</strong>&nbsp;&nbsp;Recording: <strong>{recSec}s</strong></span>}
      />
      <div className="exam-split" style={{flex:1,display:'flex',overflow:'hidden'}}>
        {/* LEFT: prompt + image */}
        <div className="exam-pane exam-pane-left" style={{width:'50%',borderRight:`1px solid ${S.panelBorder}`,padding:'20px',overflowY:'auto',background:S.white}} data-testid="speaking-left-panel">
          <div style={{display:'flex',alignItems:'flex-start',marginBottom:'14px'}}><InfoIcon/><span style={{color:S.blue,fontSize:'14px',lineHeight:'1.6',fontWeight:'600'}}><RichText value={question?.prompt_text||''}/></span></div>
          {question?.image_url && <div style={{textAlign:'center',marginTop:'12px'}}><img src={question.image_url} alt="Prompt" style={{maxWidth:'100%',maxHeight:'340px',border:'1px solid #ddd',borderRadius:'4px'}}/></div>}
          <div style={{marginTop:'18px',fontSize:'12px',color:'#c0392b',fontStyle:'italic',fontWeight:'bold'}}>*NOTE: This practice test is recording your response for AI scoring.</div>
          {question?.show_sample_responses && <div style={{marginTop:'14px',border:'2px solid #999',padding:'14px 18px',textAlign:'center',cursor:'pointer',fontSize:'14px',color:S.textDark,fontWeight:'500'}}>Click here to view sample<br/>responses.</div>}
        </div>
        {/* RIGHT: recording interface */}
        <div className="exam-pane exam-pane-right" style={{width:'50%',padding:'24px',background:'#f8fafc',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center'}} data-testid="speaking-right-panel">
          {phase==='prep' && (
            <div style={{textAlign:'center'}}>
              <div style={{background:S.white,borderRadius:'12px',padding:'28px 48px',display:'flex',alignItems:'center',gap:'20px',border:'1px solid #ddd',marginBottom:'16px',boxShadow:'0 2px 8px rgba(0,0,0,0.06)'}}>
                <span style={{fontSize:'28px'}}>&#9201;</span>
                <div><div style={{fontSize:'16px',fontWeight:'bold',color:S.textDark}}>Preparation Time</div><div style={{fontSize:'36px',fontWeight:'bold',color:S.blue}}>{pt}</div></div>
              </div>
              <div style={{fontSize:'14px',color:S.textLight}}>Recording will begin automatically</div>
              <button onClick={startRec} style={{marginTop:'18px',background:'#3b82f6',color:'#fff',border:'none',padding:'10px 22px',cursor:'pointer',fontSize:'14px',borderRadius:'4px',fontWeight:'bold',fontFamily:S.font}} data-testid="speaking-start-now-btn">
                Start Recording Now
              </button>
            </div>
          )}
          {phase==='recording' && (
            <div style={{textAlign:'center'}}>
              <div style={{width:'130px',height:'130px',borderRadius:'50%',background:'#fde8e8',border:'5px solid #e74c3c',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 20px',boxShadow:'0 0 0 8px rgba(231,76,60,0.15)'}}>
                <span style={{fontSize:'42px',fontWeight:'bold',color:'#c0392b'}}>{rt}</span>
              </div>
              <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'8px',marginBottom:'12px'}}>
                <div style={{width:'12px',height:'12px',borderRadius:'50%',background:'#e74c3c',animation:'pulse 1s infinite'}}/>
                <span style={{fontSize:'16px',fontWeight:'bold',color:'#e74c3c'}}>Recording...</span>
              </div>
              <button onClick={stopRec} style={{marginTop:'8px',background:'#e74c3c',color:'#fff',border:'none',padding:'9px 24px',cursor:'pointer',fontSize:'14px',borderRadius:'4px',fontWeight:'bold',fontFamily:S.font}} data-testid="stop-recording-btn">
                <Square size={14} style={{display:'inline',marginRight:'6px',verticalAlign:'middle'}}/>Stop Recording
              </button>
            </div>
          )}
          {phase==='uploading' && (
            <div style={{textAlign:'center',width:'100%',maxWidth:'420px'}} data-testid="recording-uploading">
              <div style={{width:'72px',height:'72px',borderRadius:'50%',border:'5px solid #dbeafe',borderTopColor:S.blue,margin:'0 auto 16px',animation:'spin 1s linear infinite'}}/>
              <div style={{fontSize:'15px',fontWeight:'bold',color:S.textDark,marginBottom:'6px'}}>Saving your response…</div>
              <div style={{fontSize:'12px',color:S.textLight}}>Uploading audio to our servers. Do not close this tab.</div>
              {localAudioURL && <audio controls src={localAudioURL} style={{width:'100%',marginTop:'14px'}} data-testid="recording-preview"/>}
              <style>{`@keyframes spin{to{transform:rotate(360deg);}}`}</style>
            </div>
          )}
          {phase==='upload_failed' && (
            <div style={{textAlign:'center',width:'100%',maxWidth:'420px'}} data-testid="recording-upload-failed">
              <div style={{width:'80px',height:'80px',borderRadius:'50%',background:'#fee2e2',border:'4px solid #dc2626',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 14px'}}>
                <span style={{fontSize:'34px',color:'#dc2626'}}>&#9888;</span>
              </div>
              <div style={{fontSize:'15px',fontWeight:'bold',color:'#dc2626',marginBottom:'8px'}}>Upload failed</div>
              <div style={{fontSize:'13px',color:S.textBody,marginBottom:'14px'}}>{uploadError || 'Upload failed — please retry.'}</div>
              {localAudioURL && <audio controls src={localAudioURL} style={{width:'100%',marginBottom:'12px'}}/>}
              <button onClick={retryUpload}
                style={{background:S.blue,color:'#fff',border:'none',padding:'10px 28px',fontSize:'14px',fontWeight:'bold',borderRadius:'4px',cursor:'pointer',fontFamily:S.font}}
                data-testid="recording-retry-btn">
                Retry Upload
              </button>
              <div style={{fontSize:'11px',color:S.textLight,marginTop:'10px'}}>Next is disabled until your recording is saved.</div>
            </div>
          )}
          {phase==='no_audio' && (
            <NoAudioPanel onRecordAgain={recordAgain} onSkip={() => { onComplete?.({ transcript: '(No audio captured)', audio_url: '' }); setPhase('done'); }} testIdPrefix="speaking-no-audio" />
          )}
          {phase==='mic_denied' && (
            <div style={{textAlign:'center',width:'100%',maxWidth:'440px'}} data-testid="mic-denied">
              <div style={{width:'80px',height:'80px',borderRadius:'50%',background:'#fef3c7',border:'4px solid #d97706',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 14px'}}>
                <span style={{fontSize:'34px',color:'#d97706'}}>&#128265;</span>
              </div>
              <div style={{fontSize:'15px',fontWeight:'bold',color:'#b45309',marginBottom:'8px'}}>Microphone not available</div>
              <div style={{fontSize:'13px',color:S.textBody,marginBottom:'14px',lineHeight:'1.55'}}>
                {micRetryCount === 0
                  ? <>Your browser blocked microphone access. <strong>Tap the button below to enable it</strong> — most browsers require this from a button click.</>
                  : <>Still blocked. Check the lock&nbsp;icon in your address bar → set Microphone to <strong>Allow</strong> for this site, then tap <em>Try again</em>. On Safari you may need to fully reload the tab.</>
                }
              </div>
              <div style={{display:'flex',gap:'8px',justifyContent:'center',flexWrap:'wrap'}}>
                <button onClick={retryMicGesture}
                  style={{background:'#2563eb',color:'#fff',border:'none',padding:'10px 24px',fontSize:'13px',fontWeight:'bold',borderRadius:'4px',cursor:'pointer',fontFamily:S.font}}
                  data-testid="mic-denied-retry-btn">
                  &#x1F3A4; Enable Microphone
                </button>
                <button onClick={skipBecauseMicDenied}
                  style={{background:'#64748b',color:'#fff',border:'none',padding:'10px 22px',fontSize:'13px',borderRadius:'4px',cursor:'pointer',fontFamily:S.font}}
                  data-testid="mic-denied-skip-btn">
                  Skip this question
                </button>
              </div>
            </div>
          )}
          {phase==='done' && (
            <div style={{textAlign:'center',width:'100%',maxWidth:'420px'}} data-testid="recording-done">
              <div style={{width:'80px',height:'80px',borderRadius:'50%',background:'#d4edda',border:'4px solid #27ae60',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 16px'}}>
                <span style={{fontSize:'32px',color:'#27ae60'}}>&#10003;</span>
              </div>
              <div style={{fontSize:'16px',fontWeight:'bold',color:'#27ae60',marginBottom:'10px'}}>Response Recorded</div>
              {playbackURL && <audio controls src={playbackURL} style={{width:'100%',marginBottom:'12px'}} data-testid="recording-playback"/>}
              <div style={{background:'#eef4ff',border:'1px solid #c8d6f0',borderRadius:'6px',padding:'12px 14px',fontSize:'12px',color:S.textBody,lineHeight:'1.55'}}>
                Your AI-scored feedback for this task will appear on the <strong>Results page</strong> once you finish the exam.
              </div>
            </div>
          )}
        </div>
      </div>
      <Foot onBack={onBack}/>
    </div>
  );
}

/* ─── speaking_compare — Speaking Task 5: Comparing and Persuading ─── */
function StepSpeakingCompare({step, title, testNum, question, onNext, onBack, onComplete, onHome, attemptId}) {
  const opts = (question?.options || []).slice(0, 2);
  const A = opts[0] || {id:'A', label:'Option A', image_url:'', description_lines:[]};
  const B = opts[1] || {id:'B', label:'Option B', image_url:'', description_lines:[]};
  const selSec = question?.selection_time_seconds || 60;
  const prepSec = question?.prep_time_seconds || 60;
  const recSec = question?.response_time_seconds || 60;

  const [phase, setPhase] = useState('choose'); // choose → prep → recording → uploading → done | upload_failed | mic_denied
  const [selected, setSelected] = useState(null); // 'A' or 'B'
  const [st, setSt] = useState(selSec);
  const [pt, setPt] = useState(prepSec);
  const [rt, setRt] = useState(recSec);
  const [localAudioURL, setLocalAudioURL] = useState(null);
  const [serverAudioURL, setServerAudioURL] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [transcript, setTranscript] = useState('');
  const [aiScore, setAiScore] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [micRetryCount, setMicRetryCount] = useState(0);
  const mr = useRef(null);
  const lastBlob = useRef(null);
  const streamRef = useRef(null); // mic stream — see StepSpeakingTask comment

  // Selection countdown — auto-pick if time runs out
  useEffect(() => {
    if (phase !== 'choose') return;
    if (st <= 0) { setSelected(sel => sel || 'A'); setPhase('prep'); return; }
    const t = setTimeout(() => setSt(v => v - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, st]);

  // Prep countdown — auto-starts recording
  useEffect(() => {
    if (phase !== 'prep') return;
    if (pt <= 0) { startRec(); return; }
    const t = setTimeout(() => setPt(v => v - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [phase, pt]);

  // Recording countdown — auto-stops
  useEffect(() => {
    if (phase !== 'recording') return;
    if (rt <= 0) { stopRec(); return; }
    const t = setTimeout(() => setRt(v => v - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [phase, rt]);

  // Cleanup on unmount (user clicks Next mid-recording / question changes via key)
  useEffect(() => () => {
    try {
      if (mr.current && mr.current.state === 'recording') mr.current.stop();
      mr.current?.stream?.getTracks()?.forEach(t => t.stop());
      streamRef.current?.getTracks()?.forEach(t => t.stop());
    } catch {}
    try { if (localAudioURL) URL.revokeObjectURL(localAudioURL); } catch {}
    // eslint-disable-next-line
  }, []);

  const chooseOption = async (id) => {
    // Toggle/replace the student's pick — DOES NOT advance the phase.
    // The student can click either card as many times as they like until
    // they press NEXT or the selection timer hits zero. (Previously this
    // function would auto-advance after 250ms, which both blocked changing
    // the mind AND caused the "right-option stuck" race on slower devices.)
    setSelected(id);

    // Acquire the mic NOW — this click is a fresh user gesture, which Safari
    // and Chrome both require for getUserMedia. We hold the stream open so
    // MediaRecorder can start without a second permission prompt. The async
    // work here does NOT block the UI (we fire-and-forget).
    if (!streamRef.current || streamRef.current.getTracks().every(t => t.readyState !== 'live')) {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = s;
      } catch (e) {
        console.warn('Mic acquisition on chooseOption failed:', e?.name || e);
        // Don't block the flow — we'll surface the mic_denied UI when prep ends
        // and startRec re-attempts (with a Try-Again button on a fresh click).
      }
    }
  };

  // User clicks "NEXT" on the choose screen once they're happy with their pick.
  const confirmChoice = () => {
    if (!selected) return;
    setPhase('prep');
  };

  // Preload the image assets the student will see on the prep/record screen
  // so when they advance there's no blank-flash while the browser fetches
  // the comparison_option image from Azure Blob. Runs once per question.
  useEffect(() => {
    const urls = [
      A?.image_url,
      B?.image_url,
      question?.comparison_option?.image_url,
    ].filter(Boolean);
    urls.forEach(url => { const img = new Image(); img.src = url; });
    // eslint-disable-next-line
  }, [question?.id]);

  // User-gesture-safe mic retry (called from a button click)
  const retryMicGesture = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = s;
      setMicRetryCount(c => c + 1);
      setPt(prepSec);
      setPhase('prep');
    } catch (e) {
      console.error('compare retry getUserMedia failed:', e);
      setMicRetryCount(c => c + 1);
    }
  };

  const skipBecauseMicDenied = () => {
    onComplete?.({
      transcript: '(Microphone not available)',
      audio_url: '',
      selected_option: selected,
    });
    setPhase('done');
  };

  const uploadBlob = async (blob) => {
    setPhase('uploading');
    setUploadError('');
    const chosenOpt = selected === 'A' ? A : B;
    // CELPIP S5 compares the student's pick against the "Sister's Choice"
    // (question.comparison_option), NOT the unselected A/B option. The student
    // sees the Sister's Choice on the prep+record screens — the AI must score
    // them against the same thing they saw.
    const cmpOpt = question?.comparison_option || {};
    const sisterHasContent = !!(cmpOpt.label || cmpOpt.image_url || (cmpOpt.description_lines || []).some(Boolean));
    const sisterChoice = sisterHasContent
      ? { label: cmpOpt.label || '', description_lines: cmpOpt.description_lines || [] }
      : (selected === 'A' ? B : A); // legacy fallback for questions without comparison_option authored
    const otherLabel = sisterChoice.label || '';
    const chosenDesc = (chosenOpt.description_lines || []).filter(Boolean).join(' • ');
    const otherDesc = (sisterChoice.description_lines || []).filter(Boolean).join(' • ');
    // Generic compare-heading per question — "Your Sister's Choice" / "Your
    // Brother's Choice" / "Your Boss's Choice" etc. Default keeps backward compat.
    const compareHdg = (cmpOpt && typeof cmpOpt.heading === 'string' && cmpOpt.heading.trim())
      || question?.comparison_heading
      || "Your Sister's Choice";
    try {
      const fd = new FormData();
      fd.append('file', blob, fileNameFor(blob.type || ''));
      fd.append('question_id', question?.id || '');
      fd.append('selected_option', selected || '');
      fd.append('selected_label', chosenOpt.label || '');
      fd.append('other_label', otherLabel);
      fd.append('chosen_description', chosenDesc);
      fd.append('other_description', otherDesc);
      fd.append('compare_heading', compareHdg);
      const res = await postAudio('/api/speaking/transcribe', fd);
      const { transcript: tx = '', ai_score = null, audio_url = '' } = res.data || {};
      setTranscript(tx);
      setAiScore(ai_score);
      setServerAudioURL(audio_url);
      setPhase('done');
      // Only fire onComplete once we have a persistent audio_url
      onComplete?.({
        transcript: tx,
        ai_score,
        audio_url,
        selected_option: selected,
        chosen_label: chosenOpt.label,
      });
    } catch (err) {
      console.error('Speaking (compare) upload failed:', err);
      setUploadError(uploadErrorMessage(err));
      setPhase('upload_failed');
    }
  };

  const startRec = async () => {
    try {
      // Reuse stream acquired earlier (chooseOption click). Falls back to a
      // fresh request if we lost it — but on Safari/iOS a fresh request from
      // this timer-driven path will likely fail; that's why we acquire eagerly.
      let s = streamRef.current;
      if (!s || s.getTracks().every(t => t.readyState !== 'live')) {
        s = await navigator.mediaDevices.getUserMedia({audio: true});
        streamRef.current = s;
      }
      mr.current = startRecorder(s, async (blob) => {
        s.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        if (isEmptyRecording(blob)) { setPhase('no_audio'); return; }
        lastBlob.current = blob;
        setAudioBlob(blob);
        setLocalAudioURL(URL.createObjectURL(blob));
        await uploadBlob(blob);
      });
      setPhase('recording');
    } catch (e) {
      console.error('getUserMedia failed (compare):', e);
      setPhase('mic_denied');
    }
  };
  const stopRec = () => { if (mr.current?.state === 'recording') mr.current.stop(); };

  const retryUpload = () => {
    if (lastBlob.current) uploadBlob(lastBlob.current);
  };

  const recordAgain = async () => {
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      setRt(recSec);
      setPt(5);
      setPhase('prep');
    } catch (e) {
      console.error('compare record-again getUserMedia failed:', e);
      setPhase('mic_denied');
    }
  };

  // Card rendering helper — CELPIP-authentic selection feedback:
  // hover lifts the card slightly, selected state draws a cyan ring +
  // checkmark badge, both with smooth 180ms transitions.
  const OptCard = ({opt, highlighted, onClick, label, clickable}) => (
    <div
      onClick={clickable ? onClick : undefined}
      style={{
        flex: 1,
        position: 'relative',
        border: `2px solid ${highlighted ? '#1a936f' : '#d5d8dc'}`,
        background: highlighted ? '#eafbf0' : S.white,
        padding: '14px',
        borderRadius: '6px',
        cursor: clickable ? 'pointer' : 'default',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        boxShadow: highlighted
          ? '0 0 0 4px rgba(26,147,111,0.18), 0 2px 6px rgba(0,0,0,0.06)'
          : '0 1px 2px rgba(0,0,0,0.04)',
        transform: highlighted ? 'translateY(-2px)' : 'translateY(0)',
        transition: 'all 180ms cubic-bezier(0.4, 0, 0.2, 1)',
      }}
      onMouseEnter={clickable ? (e) => { if (!highlighted) { e.currentTarget.style.borderColor = '#8ab4d8'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 10px rgba(0,0,0,0.08)'; } } : undefined}
      onMouseLeave={clickable ? (e) => { if (!highlighted) { e.currentTarget.style.borderColor = '#d5d8dc'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.04)'; } } : undefined}
      data-testid={`compare-card-${opt.id}`}
    >
      {/* Selected-state checkmark badge */}
      {highlighted && clickable && (
        <div style={{position:'absolute',top:'-10px',right:'-10px',width:'28px',height:'28px',borderRadius:'50%',background:'#1a936f',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'16px',fontWeight:'bold',boxShadow:'0 2px 4px rgba(0,0,0,0.15)',animation:'compare-pop 200ms cubic-bezier(0.34,1.56,0.64,1)'}} aria-hidden>
          ✓
        </div>
      )}
      {label && <div style={{textAlign: 'center', fontSize: '13px', fontWeight: 'bold', color: highlighted ? '#1a936f' : S.textBody}}>{label}</div>}
      {opt.image_url ? (
        <img src={opt.image_url} alt={opt.label || opt.id} loading="eager" style={{width: '100%', maxHeight: '260px', objectFit: 'contain', borderRadius: '3px', border: '1px solid #e5e7eb', background: '#fff'}} />
      ) : (
        <div style={{width: '100%', height: '200px', background: '#f1f5f9', border: '1px dashed #cbd5e1', borderRadius: '3px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '13px'}}>No image uploaded</div>
      )}
      {opt.label && <div style={{fontSize: '14px', fontWeight: '600', color: S.textDark}}>{opt.label}</div>}
      <ul style={{margin: 0, paddingLeft: '18px', color: S.textBody, fontSize: '13px', lineHeight: '1.7'}}>
        {(opt.description_lines || []).filter(Boolean).map((l, i) => <li key={i} style={{listStyle: 'none', position: 'relative'}}><span style={{position:'absolute', left:'-14px'}}>-</span>{l}</li>)}
      </ul>
    </div>
  );

  // PHASE: CHOOSE ─────────────────────────────────────────────
  if (phase === 'choose') {
    const timerLow = st <= 10;
    return (
      <div style={{display: 'flex', flexDirection: 'column', height: '100%', fontFamily: S.font, animation: 'compare-fade 220ms ease-out'}}>
        <style>{`
          @keyframes compare-fade { from {opacity:0; transform:translateY(6px);} to {opacity:1; transform:translateY(0);} }
          @keyframes compare-pop { 0% {transform:scale(0); opacity:0;} 60% {transform:scale(1.15); opacity:1;} 100% {transform:scale(1); opacity:1;} }
          @keyframes compare-pulse { 0%,100% {box-shadow:0 0 0 0 rgba(192,57,43,0.45);} 50% {box-shadow:0 0 0 6px rgba(192,57,43,0);} }
          @keyframes compare-btn-pulse { 0%,100% {box-shadow:0 2px 4px rgba(11,92,173,0.3);} 50% {box-shadow:0 3px 12px rgba(11,92,173,0.5);} }
        `}</style>
        <Header
          title={`Practice Test ${testNum} - ${title}`}
          timeRemaining={null}
          onNext={selected ? confirmChoice : undefined}
          showNext={!!selected}
          onHome={onHome}
          rightExtra={<span style={{fontSize: '12px', color: S.hdrText}}>Selection: <strong>{selSec}s</strong></span>}
        />
        <div className="exam-body" style={{flex: 1, overflowY: 'auto', padding: '18px 24px', background: S.white}}>
          <div style={{display: 'flex', alignItems: 'flex-start', marginBottom: '18px'}}>
            <InfoIcon />
            <div style={{color: S.blue, fontSize: '14px', lineHeight: '1.6'}}>
              <RichText value={question?.compare_intro_text || 'Using the pictures and information below, choose the option that you prefer. In the next section, you will need to persuade someone that your choice is the better choice.'}/>
              <div style={{marginTop: '6px'}}>If you do not choose an option, the computer will choose one for you. You do not need to speak for this part.</div>
            </div>
          </div>
          <div className="exam-compare-cards" style={{display: 'flex', gap: '18px', marginBottom: '18px'}}>
            <OptCard opt={A} highlighted={selected === 'A'} clickable onClick={() => chooseOption('A')} />
            <OptCard opt={B} highlighted={selected === 'B'} clickable onClick={() => chooseOption('B')} />
          </div>
          <div style={{display: 'flex', justifyContent: 'center', marginBottom: '10px'}}>
            <div
              style={{
                background: timerLow ? '#fde8e8' : '#e9edf3',
                borderRadius: '10px',
                padding: '14px 48px',
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                border: timerLow ? '1px solid #e74c3c' : '1px solid transparent',
                transition: 'background 240ms, border-color 240ms',
              }}
              data-testid="compare-choose-timer"
            >
              <span style={{fontSize: '22px'}}>&#9201;</span>
              <div style={{textAlign: 'center'}}>
                <div style={{fontSize: '13px', color: timerLow ? '#c0392b' : S.textBody, fontWeight: '600'}}>Selection Time</div>
                <div style={{fontSize: '26px', fontWeight: 'bold', color: timerLow ? '#c0392b' : S.blue, lineHeight: '1.1'}}>{st}s</div>
              </div>
            </div>
          </div>
          {/* Helper + confirm CTA */}
          <div className="exam-compare-confirm" style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:'14px', marginTop:'14px', padding:'12px 16px', background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:'6px'}}>
            <div style={{fontSize: '13px', color: selected ? '#1a936f' : '#64748b'}}>
              {selected
                ? <span><strong>Option {selected} selected.</strong> You can still change your mind, or press <strong>Next</strong> to confirm.</span>
                : <span>Click on an option to choose it — you can change your pick any time before the timer runs out.</span>
              }
            </div>
            <button
              onClick={confirmChoice}
              disabled={!selected}
              style={{
                background: selected ? S.blue : '#cbd5e1',
                color: '#fff',
                border: 'none',
                padding: '10px 28px',
                fontSize: '14px',
                fontWeight: 'bold',
                borderRadius: '4px',
                cursor: selected ? 'pointer' : 'not-allowed',
                fontFamily: S.font,
                letterSpacing: '0.5px',
                animation: selected ? 'compare-btn-pulse 1.6s ease-in-out infinite' : 'none',
                transition: 'background 160ms',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => { if (selected) e.currentTarget.style.background = S.blueHover; }}
              onMouseLeave={e => { if (selected) e.currentTarget.style.background = S.blue; }}
              data-testid="compare-choose-next-btn"
            >
              NEXT
            </button>
          </div>
        </div>
        <Foot onBack={onBack} />
      </div>
    );
  }

  // PHASE: PREP / RECORDING / DONE ────────────────────────────
  const yourChoice = selected === 'A' ? A : B;
  // CELPIP shows a 3rd option on the prep+record screens — distinct from the
  // two A/B selection options. It comes from question.comparison_option.
  // The HEADING ("Your Sister's Choice", "Your Brother's Choice", "Your Boss's
  // Choice", "Your Friend's Choice", etc.) is admin-configurable per question
  // via comparison_option.heading. Defaults to "Your Sister's Choice" for
  // backward compat with existing questions.
  // Fallback (legacy questions): if the admin hasn't authored a
  // comparison_option yet, show the option the student didn't pick.
  const cmp = question?.comparison_option || {};
  const otherChoice = (cmp && (cmp.label || cmp.image_url || (cmp.description_lines || []).some(Boolean)))
    ? { id: cmp.id || 'C', label: cmp.label || '', image_url: cmp.image_url || '', description_lines: cmp.description_lines || [] }
    : (selected === 'A' ? B : A);
  const compareHeading = (cmp && typeof cmp.heading === 'string' && cmp.heading.trim())
    || question?.comparison_heading
    || "Your Sister's Choice";
  return (
    <div style={{display: 'flex', flexDirection: 'column', height: '100%', fontFamily: S.font, animation: 'compare-fade 220ms ease-out'}}>
      <style>{`
        @keyframes compare-fade { from {opacity:0; transform:translateY(6px);} to {opacity:1; transform:translateY(0);} }
        @keyframes compare-pop { 0% {transform:scale(0); opacity:0;} 60% {transform:scale(1.15); opacity:1;} 100% {transform:scale(1); opacity:1;} }
        @keyframes pulse { 0%,100% {opacity:1; transform:scale(1);} 50% {opacity:0.55; transform:scale(0.85);} }
        @keyframes spin { to {transform:rotate(360deg);} }
      `}</style>
      <Header title={`Practice Test ${testNum} - ${title}`} timeRemaining={null} onNext={phase === 'done' ? onNext : undefined} showNext={phase === 'done'} onHome={onHome}
        rightExtra={<span style={{fontSize: '12px', color: S.hdrText}}>Preparation: <strong>{prepSec}s</strong>&nbsp;&nbsp;Recording: <strong>{recSec}s</strong></span>} />
      <div className="exam-body" style={{flex: 1, overflowY: 'auto', padding: '16px 24px', background: S.white}}>
        <div style={{display: 'flex', alignItems: 'flex-start', marginBottom: '14px'}}>
          <InfoIcon />
          <span style={{color: S.blue, fontSize: '14px', lineHeight: '1.6', fontWeight: '600'}}>
            <RichText value={question?.prompt_text || 'Persuade the other person that the option you chose is more suitable by comparing the two.'}/>
          </span>
        </div>
        <div className="exam-compare-cards" style={{display: 'flex', gap: '18px', marginBottom: '16px'}}>
          <OptCard opt={otherChoice} label={compareHeading} highlighted={false} />
          <OptCard opt={yourChoice} label="Your Choice" highlighted={true} />
        </div>

        {/* Phase indicator */}
        <div style={{display: 'flex', justifyContent: 'center', marginBottom: '12px'}}>
          {phase === 'prep' && (
            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
              <div style={{background: '#e9edf3', borderRadius: '10px', padding: '14px 48px', display: 'flex', alignItems: 'center', gap: '14px'}} data-testid="compare-prep-timer">
                <span style={{fontSize: '22px'}}>&#9201;</span>
                <div style={{textAlign: 'center'}}>
                  <div style={{fontSize: '14px', color: S.textBody, fontWeight: '600'}}>Preparation Time</div>
                  <div style={{fontSize: '24px', fontWeight: 'bold', color: S.blue}}>{pt}</div>
                </div>
              </div>
              <div style={{fontSize: '13px', color: S.textLight, marginTop: '12px'}}>Recording will begin automatically</div>
              <button onClick={startRec} style={{marginTop: '12px', background: '#3b82f6', color: '#fff', border: 'none', padding: '10px 22px', cursor: 'pointer', fontSize: '14px', borderRadius: '4px', fontWeight: 'bold', fontFamily: S.font}} data-testid="compare-start-now-btn">
                Start Recording Now
              </button>
            </div>
          )}
          {phase === 'recording' && (
            <div style={{textAlign: 'center'}} data-testid="compare-recording">
              <div style={{width: '110px', height: '110px', borderRadius: '50%', background: '#fde8e8', border: '5px solid #e74c3c', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', boxShadow: '0 0 0 8px rgba(231,76,60,0.15)'}}>
                <span style={{fontSize: '36px', fontWeight: 'bold', color: '#c0392b'}}>{rt}</span>
              </div>
              <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'}}>
                <div style={{width: '12px', height: '12px', borderRadius: '50%', background: '#e74c3c', animation: 'pulse 1s infinite'}} />
                <span style={{fontSize: '14px', fontWeight: 'bold', color: '#e74c3c'}}>Recording...</span>
              </div>
              <button onClick={stopRec} style={{marginTop: '10px', background: '#e74c3c', color: '#fff', border: 'none', padding: '7px 20px', cursor: 'pointer', fontSize: '13px', borderRadius: '4px', fontWeight: 'bold', fontFamily: S.font}} data-testid="stop-recording-btn">
                <Square size={12} style={{display: 'inline', marginRight: '6px', verticalAlign: 'middle'}} />Stop
              </button>
            </div>
          )}
          {phase === 'uploading' && (
            <div style={{textAlign: 'center', width: '100%', maxWidth: '420px'}} data-testid="compare-uploading">
              <div style={{width: '60px', height: '60px', borderRadius: '50%', border: '5px solid #dbeafe', borderTopColor: S.blue, margin: '0 auto 12px', animation: 'spin 1s linear infinite'}}/>
              <div style={{fontSize: '14px', fontWeight: 'bold', color: S.textDark, marginBottom: '4px'}}>Saving your response…</div>
              <div style={{fontSize: '12px', color: S.textLight}}>Uploading audio. Do not close this tab.</div>
              {localAudioURL && <audio controls src={localAudioURL} style={{width: '100%', marginTop: '10px'}}/>}
              <style>{`@keyframes spin{to{transform:rotate(360deg);}}`}</style>
            </div>
          )}
          {phase === 'upload_failed' && (
            <div style={{textAlign: 'center', width: '100%', maxWidth: '420px'}} data-testid="compare-upload-failed">
              <div style={{width: '64px', height: '64px', borderRadius: '50%', background: '#fee2e2', border: '4px solid #dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px'}}>
                <span style={{fontSize: '28px', color: '#dc2626'}}>&#9888;</span>
              </div>
              <div style={{fontSize: '14px', fontWeight: 'bold', color: '#dc2626', marginBottom: '6px'}}>Upload failed</div>
              <div style={{fontSize: '12px', color: S.textBody, marginBottom: '10px'}}>{uploadError || 'Upload failed — please retry.'}</div>
              {localAudioURL && <audio controls src={localAudioURL} style={{width: '100%', marginBottom: '10px'}}/>}
              <button onClick={retryUpload}
                style={{background: S.blue, color: '#fff', border: 'none', padding: '8px 22px', fontSize: '13px', fontWeight: 'bold', borderRadius: '4px', cursor: 'pointer', fontFamily: S.font}}
                data-testid="compare-retry-btn">
                Retry Upload
              </button>
              <div style={{fontSize: '11px', color: S.textLight, marginTop: '8px'}}>Next is disabled until your recording is saved.</div>
            </div>
          )}
          {phase === 'done' && (
            <div style={{textAlign: 'center', width: '100%', maxWidth: '420px'}} data-testid="compare-done">
              <div style={{width: '64px', height: '64px', borderRadius: '50%', background: '#d4edda', border: '4px solid #27ae60', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px'}}>
                <span style={{fontSize: '28px', color: '#27ae60'}}>&#10003;</span>
              </div>
              <div style={{fontSize: '15px', fontWeight: 'bold', color: '#27ae60', marginBottom: '10px'}}>Response Recorded</div>
              {(serverAudioURL || localAudioURL) && <audio controls src={serverAudioURL ? resolveMediaUrl(serverAudioURL) : localAudioURL} style={{width: '100%', marginBottom: '10px'}} data-testid="recording-playback" />}
              <div style={{background:'#eef4ff',border:'1px solid #c8d6f0',borderRadius:'6px',padding:'10px 14px',fontSize:'12px',color:S.textBody,lineHeight:'1.55'}}>
                Your AI-scored feedback for this task will appear on the <strong>Results page</strong> once you finish the exam.
              </div>
            </div>
          )}
          {phase === 'no_audio' && (
            <NoAudioPanel onRecordAgain={recordAgain} onSkip={() => { onComplete?.({ transcript: '(No audio captured)', audio_url: '', selected_option: selected }); setPhase('done'); }} testIdPrefix="compare-no-audio" />
          )}
          {phase === 'mic_denied' && (
            <div style={{textAlign: 'center', maxWidth: '460px'}} data-testid="compare-mic-error">
              <div style={{width: '70px', height: '70px', borderRadius: '50%', background: '#fef3c7', border: '4px solid #d97706', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', fontSize: '32px', color: '#d97706'}}>&#128265;</div>
              <div style={{fontSize: '15px', fontWeight: 'bold', color: '#b45309', marginBottom: '8px'}}>Microphone not available</div>
              <div style={{fontSize: '12px', color: S.textBody, lineHeight: '1.6', marginBottom: '14px'}}>
                {micRetryCount === 0
                  ? <>Your browser blocked microphone access. <strong>Tap the button below to enable it</strong> — most browsers require this from a button click.</>
                  : <>Still blocked. Click the lock&nbsp;icon in your address bar → set Microphone to <strong>Allow</strong> for this site, then tap <em>Try again</em>. On Safari you may need to fully reload the tab.</>
                }
              </div>
              <div style={{display:'flex',gap:'8px',justifyContent:'center',flexWrap:'wrap'}}>
                <button onClick={retryMicGesture} style={{background: '#2563eb', color: '#fff', border: 'none', padding: '9px 22px', cursor: 'pointer', fontSize: '13px', borderRadius: '4px', fontWeight: 'bold', fontFamily: S.font}} data-testid="compare-retry-mic-btn">
                  &#x1F3A4; Enable Microphone
                </button>
                <button onClick={skipBecauseMicDenied} style={{background: '#64748b', color: '#fff', border: 'none', padding: '9px 22px', cursor: 'pointer', fontSize: '13px', borderRadius: '4px', fontFamily: S.font}} data-testid="compare-skip-mic-btn">
                  Skip this question
                </button>
              </div>
            </div>
          )}
        </div>
        <div style={{fontSize: '12px', color: '#c0392b', fontStyle: 'italic', fontWeight: 'bold'}}>*NOTE: This practice test is recording your response for AI scoring.</div>
      </div>
      <Foot onBack={onBack} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   FALLBACK FLOW BUILDER — for sections without flow_steps
   ═══════════════════════════════════════════════════════════════ */
function autoFlow(sec) {
  if(!sec) return [];
  const steps=[];
  const qs=sec.questions||[];
  const isDropdown=qs.some(q=>q.question_type==='dropdown_fill');
  const isDropdownPara=qs.some(q=>q.question_type==='dropdown_paragraph');
  if(sec.instruction_text) steps.push({type:'text_instruction',heading:sec.name||'',content:sec.instruction_text});
  if(sec.section_context) steps.push({type:'context_image',heading:'Instructions:',content:sec.section_context,image_url:sec.context_image_url||''});
  const skill=sec.skill_id||'';
  const hasAudio=!!sec.section_audio_url;
  const hasVideo=!!sec.section_video_url;
  if(skill==='listening'){
    // Media step: prefer video if present; else audio only if configured.
    if(hasVideo) steps.push({type:'video_instruction',heading:sec.section_context||'Watch the discussion. You will watch it only once.',video_url:sec.section_video_url});
    else if(hasAudio) steps.push({type:'audio_player',heading:sec.section_context||'',audio_url:sec.section_audio_url});
    // Question step(s)
    if(isDropdownPara){
      // Each dropdown_paragraph is a self-contained page; let the step dispatcher render it.
      qs.forEach(q=>{if(q.question_type==='dropdown_paragraph') steps.push({type:'question',question_id:q.id});});
    } else if(isDropdown){
      steps.push({type:'dropdown_wide'});
    } else if(sec.question_groups?.length>0){
      const ord=['first','second','third','fourth','fifth','sixth'];
      sec.question_groups.forEach((g,i)=>{
        if(hasAudio&&i>0) steps.push({type:'audio_player',heading:sec.section_context||'',audio_url:sec.section_audio_url||''});
        steps.push({type:'mcq_split',question_range:g});
        if(i<sec.question_groups.length-1) steps.push({type:'break_timer',seconds:10,message:`You will hear the ${ord[i+1]||'next'} section of the conversation shortly.`});
      });
    } else {
      steps.push({type:'mcq_split',question_range:[0,qs.length]});
    }
  } else if(skill==='reading'){
    if(isDropdownPara){
      qs.forEach(q=>{if(q.question_type==='dropdown_paragraph') steps.push({type:'question',question_id:q.id});});
    } else if(isDropdown||sec.section_passage_text) {
      steps.push({type:'dropdown_split',left_heading:'Read the following message.',left_content:sec.section_passage_text,right_heading:'Using the drop-down menu, choose the best option.'});
    } else {
      steps.push({type:'mcq_split',question_range:[0,qs.length]});
    }
  }
  return steps;
}

/* ═══════════════════════════════════════════════════════════════
   MAIN EXAM ENGINE
   ═══════════════════════════════════════════════════════════════ */
export default function ExamTake() {
  const {examId}=useParams();
  const navigate=useNavigate();
  const [exam,setExam]=useState(null);
  const [attempt,setAttempt]=useState(null);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState('');
  const [secIdx,setSecIdx]=useState(0);
  const [stepIdx,setStepIdx]=useState(0);
  const [subQ,setSubQ]=useState(0);
  const [responses,setResponses]=useState({});
  const [timeRem,setTimeRem]=useState(null);
  const [timerOn,setTimerOn]=useState(false);
  const [exitDlg,setExitDlg]=useState(false);
  const [submitDlg,setSubmitDlg]=useState(false);
  const [submitting,setSubmitting]=useState(false);
  const [targetSkill,setTargetSkill]=useState(null);
  // Admin "Preview as Student" mode — ?preview=1. Skips attempt creation,
  // save-progress, and submit so the admin can click through the exam safely.
  const isPreview = React.useMemo(() => new URLSearchParams(window.location.search).get('preview') === '1', []);
  // Admin-only deep-link — ?preview=1&jump_to=W2 (or L1/R3/S5/etc.). Skips directly
  // to the first step of the matching section so manual/automated reviewers can
  // test a specific task without clicking through 3 hours of exam flow. Guarded:
  // ONLY honoured when preview=1 AND the caller has role "admin". Ignored in prod.
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const jumpTo = React.useMemo(() => {
    const raw = new URLSearchParams(window.location.search).get('jump_to');
    if (!raw) return null;
    if (!(isPreview && isAdmin)) return null;
    return raw.toUpperCase().trim();
  }, [isPreview, isAdmin]);

  useEffect(()=>{const p=new URLSearchParams(window.location.search);const s=p.get('section');const sk=p.get('skill');if(s!=null)setSecIdx(parseInt(s,10));if(sk)setTargetSkill(sk);},[]);
  // Resume bookkeeping — `restoredRef` flips on once the saved position has been
  // applied so the auto-save effect never overwrites real progress with 0/0/0.
  const restoredRef=useRef(false);
  const latestRef=useRef({});
  latestRef.current={secIdx,stepIdx,subQ,timeRem,responses,attemptId:attempt?.id};
  useEffect(()=>{(async()=>{try{
    if(isPreview){
      const er = await api.get(`/api/exams/${examId}`);
      setExam(er.data);
      setAttempt({id:'preview', responses:{}});
    } else {
      const[er,ar]=await Promise.all([api.get(`/api/exams/${examId}`),api.post(`/api/exams/${examId}/start`)]);
      const ex=er.data, at=ar.data;
      setExam(ex);setAttempt(at);
      if(at.responses)setResponses(at.responses);
      // Restore the exact resume point. A skill-only launch (?section=&skill=) only
      // resumes when the saved section belongs to that skill; otherwise it starts at
      // the requested section (Start Over already abandoned the old attempt → fresh 0/0/0).
      const p=new URLSearchParams(window.location.search);
      const savedSec=Number.isInteger(at.current_section_index)?at.current_section_index:0;
      const savedStep=Number.isInteger(at.current_step_index)?at.current_step_index:0;
      const savedSub=Number.isInteger(at.current_sub_q)?at.current_sub_q:0;
      const hasProgress=savedSec>0||savedStep>0||savedSub>0||Object.keys(at.responses||{}).length>0;
      const sk=p.get('skill');
      const sameSkill=!sk||(ex.sections?.[savedSec]?.skill_id===sk);
      if(hasProgress&&sameSkill){
        setSecIdx(savedSec);setStepIdx(savedStep);setSubQ(savedSub);
        if(typeof at.step_time_remaining==='number'&&at.step_time_remaining>0){setTimeRem(at.step_time_remaining);setTimerOn(true);}
      }
      restoredRef.current=true;
    }
  }catch(e){setError(e.response?.data?.detail||'Failed to load');}finally{setLoading(false);}})();},[examId, isPreview]);

  const progressPayload=useCallback(()=>{
    const s=latestRef.current;
    if(!s.attemptId||s.attemptId==='preview')return null;
    return {attempt_id:s.attemptId,current_section_index:s.secIdx,current_step_index:s.stepIdx,current_sub_q:s.subQ,
      step_time_remaining:typeof s.timeRem==='number'?s.timeRem:null,responses:s.responses};
  },[]);
  const saveProgress=useCallback(()=>{
    if(isPreview||!restoredRef.current)return Promise.resolve();
    const payload=progressPayload();
    if(!payload)return Promise.resolve();
    return api.post(`/api/exams/${examId}/save-progress`,payload).catch(()=>{});
  },[examId,isPreview,progressPayload]);
  // Auto-save on every step / answer change (debounced) …
  const saveTimer=useRef(null);
  useEffect(()=>{
    if(isPreview||!restoredRef.current)return undefined;
    clearTimeout(saveTimer.current);
    saveTimer.current=setTimeout(saveProgress,400);
    return()=>clearTimeout(saveTimer.current);
  },[secIdx,stepIdx,subQ,responses,saveProgress,isPreview]);
  // … flush a pending save when the player unmounts (header "Dashboard" link, browser Back) …
  useEffect(()=>()=>{clearTimeout(saveTimer.current);saveProgress();},[saveProgress]);
  // … every 20 s while a timer runs (keeps the remaining time fresh) …
  useEffect(()=>{
    if(isPreview||!timerOn)return undefined;
    const t=setInterval(saveProgress,20000);
    return()=>clearInterval(t);
  },[timerOn,saveProgress,isPreview]);
  // … and when the tab is closed / backgrounded (keepalive so the request survives unload).
  useEffect(()=>{
    if(isPreview)return undefined;
    const flush=()=>{
      if(!restoredRef.current)return;
      const payload=progressPayload();
      if(!payload)return;
      try{
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/exams/${examId}/save-progress`,{method:'POST',keepalive:true,
          headers:{'Content-Type':'application/json',Authorization:`Bearer ${localStorage.getItem('token')||''}`},body:JSON.stringify(payload)});
      }catch(_){/* best effort */}
    };
    const onVis=()=>{if(document.visibilityState==='hidden')flush();};
    window.addEventListener('pagehide',flush);document.addEventListener('visibilitychange',onVis);
    return()=>{window.removeEventListener('pagehide',flush);document.removeEventListener('visibilitychange',onVis);};
  },[examId,isPreview,progressPayload]);

  // Admin ?jump_to=W2 deep-link — runs once exam is loaded. Resolves the
  // jump_to part_id (e.g. W2, L5, S3) to a section index in the loaded exam
  // and fast-forwards `secIdx` (and `stepIdx`/`subQ` to 0 so we land on the
  // section's first step). No-ops when jumpTo is null (non-admin, non-preview,
  // or not set), or when no matching section exists.
  useEffect(() => {
    if (!jumpTo || !exam?.sections?.length) return;
    const idx = exam.sections.findIndex(s => (s.part_id || '').toUpperCase() === jumpTo);
    if (idx < 0) {
      console.warn(`[jump_to] No section matches part_id="${jumpTo}" in this exam.`);
      return;
    }
    setSecIdx(idx);
    setStepIdx(0);
    setSubQ(0);
    stopTimer();
    setTimeRem(null);
    // eslint-disable-next-line
  }, [jumpTo, exam]);

  // Timer - with safety check to prevent hanging
  useEffect(()=>{
    if(!timerOn||timeRem==null) return;
    if(timeRem<=0){setTimerOn(false);return;} // Don't advance from timer - let user click NEXT
    const t=setTimeout(()=>setTimeRem(v=>Math.max(0,v-1)),1000);
    return()=>clearTimeout(t);
  },[timerOn,timeRem]);

  const sec=exam?.sections?.[secIdx];
  const qs=sec?.questions||[];
  const qMap={}; qs.forEach(q=>{qMap[q.id]=q;}); // question lookup map
  const flow=sec?.flow_steps?.length>0?sec.flow_steps:autoFlow(sec);
  const curStep=flow[stepIdx];
  const testNum=exam?.exam_number||1;
  const title=partAwareTitle(sec);
  const isLast=secIdx>=(exam?.sections?.length||0)-1;

  const goHome=()=>{setExitDlg(true);};
  const [exiting,setExiting]=useState(false);
  const doExit=async()=>{setExiting(true);clearTimeout(saveTimer.current);await saveProgress();navigate(`/exams/${examId}`);triggerExitIntent('test_exit');};
  const onResp=useCallback((qid,val)=>setResponses(p=>({...p,[qid]:val})),[]);

  const startTimer=useCallback((secs, sectionOverride)=>{
    const t=secs||(sectionOverride||sec)?.time_limit_seconds;
    if(t && t > 0){setTimeRem(t);setTimerOn(true);}
  },[sec]);
  const stopTimer=useCallback(()=>{setTimerOn(false);},[]);

  const goNextStep=useCallback(()=>{
    const nx=stepIdx+1;
    if(nx<flow.length){
      setStepIdx(nx);setSubQ(0);
      const ns=flow[nx];
      // Reset timer for new step
      stopTimer(); setTimeRem(null);
      // Start per-step timer if set, or question steps use section timer
      if(ns?.timer_seconds && ns.timer_seconds > 0){startTimer(ns.timer_seconds);}
      else if(['question','question_group'].includes(ns?.type)){startTimer();}
    } else goNextSec();
  },[flow,stepIdx,startTimer,stopTimer]);

  const goNextSec=useCallback(()=>{
    stopTimer(); setTimeRem(null);
    if(!isLast){
      const nx=secIdx+1;
      const ns=exam?.sections?.[nx];
      if(targetSkill&&ns&&ns.skill_id!==targetSkill){
        // End of target skill — show submit confirmation
        setSubmitDlg(true);
        return;
      }
      setSecIdx(nx);setStepIdx(0);setSubQ(0);
      // Kick off the timer for the NEW section's first step here (using the new
      // section for the default-timer fallback). Without this, a section whose
      // first step is a timed question relied on a render-phase setState, which
      // could leave the timer hidden. Screen/audio first-steps start no timer;
      // the per-step timer then starts when goNextStep reaches the question.
      const nsFlow = (ns.flow_steps && ns.flow_steps.length>0) ? ns.flow_steps : ((ns.steps && ns.steps.length>0) ? ns.steps : null);
      const firstStep = nsFlow ? nsFlow[0] : null;
      if(firstStep?.timer_seconds && firstStep.timer_seconds>0){ startTimer(firstStep.timer_seconds, ns); }
      else if(firstStep && ['question','question_group'].includes(firstStep.type)){ startTimer(undefined, ns); }
    } else {
      // Last section — show submit confirmation
      setSubmitDlg(true);
    }
  },[isLast,secIdx,exam,targetSkill,stopTimer,startTimer]);

  const doSubmit=async()=>{
    setSubmitDlg(false);
    if(isPreview){
      // Preview mode — just bounce back to the admin exams list instead of scoring.
      navigate('/admin/exams');
      return;
    }
    setSubmitting(true);
    try{
      // Submit can be slow under load (per-question DB inserts on Cosmos).
      // Give it a generous 90s window so the network layer doesn't abort
      // a successful submit just because Cosmos is briefly hot. The actual
      // AI scoring now runs in the background (iter60) and does NOT block
      // this call.
      const res = await api.post(`/api/exams/${examId}/submit`,{
        attempt_id:attempt.id,
        responses,
        target_skill: targetSkill || null,  // null = full exam
      }, { timeout: 90000 });
      const attemptId = res.data?.id || attempt.id;
      navigate(`/exams/${examId}/results/${attemptId}`, { replace: true, state: { finished: true } });
    }catch(e){
      console.error('Submit error:', e);
      // Surface a clearer message — the most common cause of a submit hang
      // used to be the speaking-transcribe endpoint stalling for 60s+ per
      // question. After iter60 that path is non-blocking, so any submit
      // failure now is genuinely network or backend-side.
      const status = e?.response?.status;
      let msg = 'Submit failed — please try again';
      if (e?.code === 'ECONNABORTED' || /timeout/i.test(e?.message || '')) {
        msg = 'Submit took too long — your answers are saved. Reopen this exam from the Exams list to view your results.';
      } else if (status === 401) {
        msg = 'Your session expired. Please log in again to submit.';
      } else if (status === 404) {
        msg = 'Exam attempt not found. Please reload and try again.';
      }
      setError(msg);
      setSubmitting(false);
    }
  };
  const goPrevStep=()=>{
    if(curStep?.type==='mcq_split'&&subQ>0){setSubQ(p=>p-1);return;}
    if(stepIdx>0){
      stopTimer(); setTimeRem(null);
      const ps=flow[stepIdx-1];
      setStepIdx(stepIdx-1);
      if(ps?.type==='mcq_split'){const r=ps.question_range||[0,qs.length];setSubQ(Math.min(r[1],qs.length)-r[0]-1);}
      else setSubQ(0);
      return;
    }
    // At first step of a non-first section → jump to the last step of previous section.
    if(secIdx>0 && exam?.sections?.length){
      const prevSecIdx = secIdx-1;
      const prevSec = exam.sections[prevSecIdx];
      if(!prevSec) return;
      const prevQs = prevSec.questions || [];
      const prevFlow = prevSec.flow_steps?.length>0 ? prevSec.flow_steps : autoFlow(prevSec);
      if(!prevFlow.length) return;
      stopTimer(); setTimeRem(null);
      const lastIdx = prevFlow.length - 1;
      const lastStep = prevFlow[lastIdx];
      setSecIdx(prevSecIdx);
      setStepIdx(lastIdx);
      if(lastStep?.type==='mcq_split'){
        const r = lastStep.question_range||[0,prevQs.length];
        setSubQ(Math.min(r[1],prevQs.length)-r[0]-1);
      } else setSubQ(0);
    }
  };

  const mcqNext=()=>{
    if(!curStep)return;const r=curStep.question_range||[0,qs.length];const gs=Math.min(r[1],qs.length)-r[0];
    if(subQ<gs-1)setSubQ(p=>p+1);else goNextStep();
  };

  useEffect(()=>{const h=e=>{if(e.key==='Escape')setExitDlg(true);};window.addEventListener('keydown',h);return()=>window.removeEventListener('keydown',h);},[]);

  // Render helpers
  if(loading)return<div style={{height:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#f5f5f5',fontFamily:S.font}}><div style={{textAlign:'center'}}><div style={{width:'40px',height:'40px',border:'4px solid #ddd',borderTop:`4px solid ${S.blue}`,borderRadius:'50%',animation:'spin 1s linear infinite',margin:'0 auto 16px'}}/><p style={{color:'#666'}}>Loading exam...</p></div><style>{`@keyframes spin{to{transform:rotate(360deg);}}`}</style></div>;
  if(error)return<div style={{height:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#f5f5f5',fontFamily:S.font}}><div style={{textAlign:'center',color:'#c0392b'}}><p style={{fontSize:'18px',marginBottom:'16px'}}>Error: {error}</p><button onClick={()=>navigate('/exams')} style={{background:S.blue,color:'#fff',border:'none',padding:'8px 24px',cursor:'pointer',borderRadius:'3px'}}>Back to Exams</button></div></div>;
  if(!sec)return<div style={{height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>{submitting?'Submitting...':'No sections'}</div>;

  const renderStep=()=>{
    if(!curStep){
      // Fallback for writing/speaking without flow_steps
      const q=qs[subQ];
      if(!q){
        return<div style={{display:'flex',flexDirection:'column',height:'100%',fontFamily:S.font}}>
          <Header title={title} onNext={goNextSec} showNext={true} timeRemaining={null} onHome={goHome}/>
          <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',background:S.white,padding:'40px'}}>
            <div style={{textAlign:'center'}}>
              <p style={{fontSize:'18px',color:S.textDark,fontWeight:'600',marginBottom:'12px'}}>No questions loaded for this section</p>
              <p style={{fontSize:'14px',color:S.textLight,marginBottom:'24px'}}>The questions may have been removed. Click NEXT to continue.</p>
              <button onClick={goNextSec} style={{background:S.nextBg,color:'#fff',border:'none',padding:'10px 28px',fontWeight:'bold',fontSize:'14px',cursor:'pointer',borderRadius:'3px',fontFamily:S.font}}>NEXT</button>
            </div>
          </div>
          <Foot onBack={goPrevStep}/>
        </div>;
      }
      if(!timerOn&&timeRem==null&&sec?.time_limit_seconds)startTimer();
      if(q.question_type==='writing_email'||q.question_type==='writing_survey')
        return<StepWritingTask step={{}} title={title} testNum={testNum} question={q} value={responses[q.id]||{}} onResponse={patch=>onResp(q.id,{...(responses[q.id]||{}),...patch})} timeRemaining={timeRem} onNext={()=>{if(subQ<qs.length-1)setSubQ(p=>p+1);else goNextSec();}} onBack={()=>{if(subQ>0)setSubQ(p=>p-1);}} onHome={goHome}/>;
      if(q.question_type==='dropdown_paragraph')
        return<StepDropdownParagraph key={q.id} step={{}} title={title} testNum={testNum} question={q} response={responses[q.id]||{}} onSelect={(qid,picks)=>onResp(qid,{selected_options:picks})} timeRemaining={timeRem} onNext={()=>{if(subQ<qs.length-1)setSubQ(p=>p+1);else goNextSec();}} onBack={()=>{if(subQ>0)setSubQ(p=>p-1);}} onHome={goHome}/>;
      if(q.question_type==='speaking_record')
        return q.is_compare_task
          ? <StepSpeakingCompare key={q.id} step={{}} title={title} testNum={testNum} question={q} attemptId={attempt?.id} onNext={()=>{if(subQ<qs.length-1)setSubQ(p=>p+1);else goNextSec();}} onBack={()=>{if(subQ>0)setSubQ(p=>p-1);}} onComplete={d=>onResp(q.id,d)} onHome={goHome}/>
          : <StepSpeakingTask key={q.id} step={{}} title={title} testNum={testNum} question={q} attemptId={attempt?.id} timeRemaining={timeRem} onNext={()=>{if(subQ<qs.length-1)setSubQ(p=>p+1);else goNextSec();}} onBack={()=>{if(subQ>0)setSubQ(p=>p-1);}} onComplete={d=>onResp(q.id,d)} onHome={goHome}/>;
      return null;
    }

    const st = curStep.type;

    // ─── SECTION RESULT / SCORE PAGE (mid-exam checkpoint) ──────
    if (st === 'section_result') {
      return <SectionResultPanel key={curStep.id || 'section-result'} examId={examId} attemptId={attempt?.id} responses={responses} skills={curStep.result_skills || []} heading={curStep.heading} title={title} onContinue={goNextStep} onHome={goHome} isPreview={isPreview} />;
    }

    // ─── SCREEN PAGE (universal) ────────────────────────────
    if (st === 'screen') {
      const hasAudio = !!(curStep.audio_url || '').trim();
      const hasVideo = !!(curStep.video_url || '').trim();
      const hasTimer = curStep.timer_seconds > 0;

      // Audio page: NEXT hidden until audio ends
      if (hasAudio) return <StepAudioPlayer step={curStep} title={title} testNum={testNum} onNext={goNextStep} onBack={goPrevStep} onHome={goHome} onAudioEnd={()=>{}}/>;
      // Video page: skip button
      if (hasVideo) return <StepVideoInstruction step={curStep} title={title} testNum={testNum} onNext={goNextStep} onBack={goPrevStep} onHome={goHome}/>;
      // Timer/break page: auto-advances
      if (hasTimer) return <StepBreakTimer step={{...curStep, message: curStep.content || curStep.heading || 'The next section will begin shortly.', seconds: curStep.timer_seconds}} title={title} testNum={testNum} onNext={goNextStep} onBack={goPrevStep} onHome={goHome}/>;
      // Image + text: context page
      if (curStep.image_url) return <StepContextImage step={curStep} title={title} testNum={testNum} onNext={goNextStep} onBack={goPrevStep} onHome={goHome}/>;
      // Default: text instruction page
      return <StepTextInstruction step={curStep} title={title} testNum={testNum} onNext={goNextStep} onBack={goPrevStep} onHome={goHome}/>;
    }

    // ─── QUESTION (single, auto-detect type) ────────────────
    if (st === 'question') {
      const q = qMap[curStep.question_id] || qs[0];
      if (!q) return null;
      if (!timerOn && timeRem == null) startTimer(curStep.timer_seconds);
      const qt = q.question_type;
      // Global position across all question steps of the same qt in this section
      // so the "Question X of Y" counter reflects the whole section, not 1-of-1.
      const sameTypeQSteps = flow
        .map((s, i) => (s?.type === 'question' ? i : -1))
        .filter(i => i >= 0)
        .filter(i => {
          const sq = qMap[flow[i]?.question_id];
          return sq && sq.question_type === qt;
        });
      const qPos = sameTypeQSteps.indexOf(stepIdx) + 1;
      const qTot = sameTypeQSteps.length;
      const mcqLabel = qTot > 1 ? `Question ${qPos} of ${qTot}` : undefined;

      if (qt === 'mcq') return <StepMCQSplit step={curStep} title={title} testNum={testNum} questions={[q]} qIdx={0} responses={responses} onSelect={(qid,v)=>onResp(qid,{selected_option:v})} timeRemaining={timeRem} onNext={goNextStep} onBack={goPrevStep} onHome={goHome} section={sec} counterLabel={mcqLabel}/>;
      if (qt === 'dropdown_fill') return <StepDropdownWide step={curStep} title={title} testNum={testNum} questions={[q]} responses={responses} onSelect={(qid,v)=>onResp(qid,{selected_option:v})} timeRemaining={timeRem} onNext={goNextStep} onBack={goPrevStep} onHome={goHome}/>;
      if (qt === 'dropdown_paragraph') return <StepDropdownParagraph key={q.id} step={curStep} title={title} testNum={testNum} question={q} response={responses[q.id]||{}} onSelect={(qid,picks)=>onResp(qid,{selected_options:picks})} timeRemaining={timeRem} onNext={goNextStep} onBack={goPrevStep} onHome={goHome}/>;
      if (qt === 'writing_email' || qt === 'writing_survey') return <StepWritingTask step={curStep} title={title} testNum={testNum} question={q} value={responses[q.id]||{}} onResponse={patch=>onResp(q.id,{...(responses[q.id]||{}),...patch})} timeRemaining={timeRem} onNext={goNextStep} onBack={goPrevStep} onHome={goHome}/>;
      if (qt === 'speaking_record') return q.is_compare_task
        ? <StepSpeakingCompare key={q.id} step={curStep} title={title} testNum={testNum} question={q} attemptId={attempt?.id} onNext={goNextStep} onBack={goPrevStep} onComplete={d=>onResp(q.id,d)} onHome={goHome}/>
        : <StepSpeakingTask key={q.id} step={curStep} title={title} testNum={testNum} question={q} attemptId={attempt?.id} timeRemaining={timeRem} onNext={goNextStep} onBack={goPrevStep} onComplete={d=>onResp(q.id,d)} onHome={goHome}/>;
      // Fallback MCQ
      return <StepMCQSplit step={curStep} title={title} testNum={testNum} questions={[q]} qIdx={0} responses={responses} onSelect={(qid,v)=>onResp(qid,{selected_option:v})} timeRemaining={timeRem} onNext={goNextStep} onBack={goPrevStep} onHome={goHome} section={sec} counterLabel={mcqLabel}/>;
    }

    // ─── QUESTION GROUP (multiple on one page) ──────────────
    if (st === 'question_group') {
      const gqs = (curStep.question_ids || []).map(id => qMap[id]).filter(Boolean);
      if (!gqs.length) return null;
      if (!timerOn && timeRem == null) startTimer(curStep.timer_seconds);
      // Shape-aware onSelect for question_group: dropdown_paragraph pushes a
      // patch object {selected_options, _kind}, simple dropdown pushes a string.
      // We branch here so both shapes can coexist in the same group.
      const groupOnSelect = (qid, v) => {
        if (v && typeof v === 'object' && v._kind === 'dropdown_paragraph') {
          // eslint-disable-next-line no-unused-vars
          const {_kind, ...rest} = v;
          onResp(qid, rest);
        } else {
          onResp(qid, {selected_option: v});
        }
      };
      // If has left panel content → split screen
      if (curStep.left_content || curStep.left_heading || curStep.left_image_url) {
        return <StepDropdownSplit step={curStep} title={title} testNum={testNum} questions={gqs} responses={responses} onSelect={groupOnSelect} timeRemaining={timeRem} onNext={goNextStep} onBack={goPrevStep} onHome={goHome} section={sec}/>;
      }
      // Otherwise full-width
      return <StepDropdownWide step={curStep} title={title} testNum={testNum} questions={gqs} responses={responses} onSelect={groupOnSelect} timeRemaining={timeRem} onNext={goNextStep} onBack={goPrevStep} onHome={goHome}/>;
    }

    // ─── LEGACY step types (backward compat) ────────────────
    switch(st){
      case 'screen': {
        // Safety: handle screen type in legacy block too
        const ha2=!!(curStep.audio_url||'').trim(); const hv2=!!(curStep.video_url||'').trim(); const ht2=curStep.timer_seconds>0;
        if(ha2) return<StepAudioPlayer step={curStep} title={title} testNum={testNum} onNext={goNextStep} onBack={goPrevStep} onHome={goHome} onAudioEnd={()=>{}}/>;
        if(hv2) return<StepVideoInstruction step={curStep} title={title} testNum={testNum} onNext={goNextStep} onBack={goPrevStep} onHome={goHome}/>;
        if(ht2) return<StepBreakTimer step={{...curStep,message:curStep.content||curStep.heading||'',seconds:curStep.timer_seconds}} title={title} testNum={testNum} onNext={goNextStep} onBack={goPrevStep} onHome={goHome}/>;
        if(curStep.image_url) return<StepContextImage step={curStep} title={title} testNum={testNum} onNext={goNextStep} onBack={goPrevStep} onHome={goHome}/>;
        return<StepTextInstruction step={curStep} title={title} testNum={testNum} onNext={goNextStep} onBack={goPrevStep} onHome={goHome}/>;
      }
      case 'text_instruction': return<StepTextInstruction step={curStep} title={title} testNum={testNum} onNext={goNextStep} onBack={goPrevStep} onHome={goHome}/>;
      case 'video_instruction': return<StepVideoInstruction step={curStep} title={title} testNum={testNum} onNext={goNextStep} onBack={goPrevStep} onHome={goHome}/>;
      case 'context_image': return<StepContextImage step={curStep} title={title} testNum={testNum} onNext={goNextStep} onBack={goPrevStep} onHome={goHome}/>;
      case 'audio_player': return<StepAudioPlayer step={curStep} title={title} testNum={testNum} onNext={goNextStep} onBack={goPrevStep} onHome={goHome} onAudioEnd={()=>{}}/>;
      case 'break_timer': return<StepBreakTimer step={curStep} title={title} testNum={testNum} onNext={goNextStep} onBack={goPrevStep} onHome={goHome}/>;
      case 'mcq_question': case 'mcq_split':{
        const r=curStep.question_range||[0,qs.length];
        const mq=curStep.question_id?[qMap[curStep.question_id]].filter(Boolean):qs.slice(r[0],Math.min(r[1],qs.length));
        if(!timerOn&&timeRem==null)startTimer(curStep.timer_seconds);
        const activeQs=mq.length?mq:qs;
        const curQ=activeQs[subQ]||activeQs[0];
        // If the current sub-question is a dropdown_paragraph, render it with the
        // dedicated renderer instead of the MCQ split (which would show radios).
        if(curQ && curQ.question_type==='dropdown_paragraph')
          return<StepDropdownParagraph key={curQ.id} step={curStep} title={title} testNum={testNum} question={curQ} response={responses[curQ.id]||{}} onSelect={(qid,picks)=>onResp(qid,{selected_options:picks})} timeRemaining={timeRem} onNext={curStep.question_id?goNextStep:mcqNext} onBack={goPrevStep} onHome={goHome}/>;
        return<StepMCQSplit step={curStep} title={title} testNum={testNum} questions={activeQs} qIdx={subQ} responses={responses} onSelect={(qid,v)=>onResp(qid,{selected_option:v})} timeRemaining={timeRem} onNext={curStep.question_id?goNextStep:mcqNext} onBack={goPrevStep} onHome={goHome} section={sec}/>;
      }
      case 'dropdown_group': case 'dropdown_wide':{
        const dqs=(curStep.question_ids||[]).map(id=>qMap[id]).filter(Boolean);
        if(!timerOn&&timeRem==null)startTimer(curStep.timer_seconds);
        return<StepDropdownWide step={curStep} title={title} testNum={testNum} questions={dqs.length?dqs:qs} responses={responses} onSelect={(qid,v)=>onResp(qid,{selected_option:v})} timeRemaining={timeRem} onNext={goNextStep} onBack={goPrevStep} onHome={goHome}/>;
      }
      case 'dropdown_split':{
        const dsqs=(curStep.question_ids||[]).map(id=>qMap[id]).filter(Boolean);
        if(!timerOn&&timeRem==null)startTimer(curStep.timer_seconds);
        return<StepDropdownSplit step={curStep} title={title} testNum={testNum} questions={dsqs.length?dsqs:qs} responses={responses} onSelect={(qid,v)=>onResp(qid,{selected_option:v})} timeRemaining={timeRem} onNext={goNextStep} onBack={goPrevStep} onHome={goHome} section={sec}/>;
      }
      case 'writing_task':{
        const wq=qMap[curStep.question_id]||qs[0];
        if(!timerOn&&timeRem==null)startTimer(curStep.timer_seconds);
        return<StepWritingTask step={curStep} title={title} testNum={testNum} question={wq} value={responses[wq?.id]||{}} onResponse={patch=>onResp(wq?.id,{...(responses[wq?.id]||{}),...patch})} timeRemaining={timeRem} onNext={goNextStep} onBack={goPrevStep} onHome={goHome}/>;
      }
      case 'speaking_task':{
        const sq=qMap[curStep.question_id]||qs[0];
        if(!timerOn&&timeRem==null)startTimer(curStep.timer_seconds);
        return sq?.is_compare_task
          ? <StepSpeakingCompare key={sq?.id} step={curStep} title={title} testNum={testNum} question={sq} attemptId={attempt?.id} onNext={goNextStep} onBack={goPrevStep} onComplete={d=>onResp(sq?.id,d)} onHome={goHome}/>
          : <StepSpeakingTask key={sq?.id} step={curStep} title={title} testNum={testNum} question={sq} attemptId={attempt?.id} timeRemaining={timeRem} onNext={goNextStep} onBack={goPrevStep} onComplete={d=>onResp(sq?.id,d)} onHome={goHome}/>;
      }
      default: return<div style={{padding:'40px',color:'#999'}}>Unknown step: {st}</div>;
    }
  };

  return (
    <ProtectedContent watermark={user?.email || ''}>
    <div className="exam-root" style={{height:'100vh',display:'flex',flexDirection:'column',background:'#eee',fontFamily:S.font}} data-testid="exam-take-page">
      {isPreview && (
        <div style={{background:'#0b6bff',color:'#fff',padding:'6px 14px',display:'flex',alignItems:'center',justifyContent:'space-between',fontFamily:S.font,fontSize:'12px',fontWeight:'600',letterSpacing:'0.3px'}} data-testid="preview-banner">
          <span>🔎 PREVIEW MODE — nothing is saved. Click through just like a student would.</span>
          <button onClick={()=>navigate('/admin/exams')} style={{background:'rgba(255,255,255,0.18)',color:'#fff',border:'1px solid rgba(255,255,255,0.4)',padding:'4px 10px',borderRadius:'4px',fontSize:'11px',fontWeight:'600',cursor:'pointer',fontFamily:S.font}} data-testid="preview-exit-btn">Close preview</button>
        </div>
      )}
      {renderStep()}
      {/* Exit Confirmation */}
      {exitDlg && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:50}} data-testid="exit-dialog">
          <div style={{background:'#fff',borderRadius:'10px',padding:'28px',maxWidth:'400px',width:'90%',boxShadow:'0 8px 32px rgba(0,0,0,0.2)'}}>
            <h3 style={{margin:'0 0 8px',fontSize:'18px',color:'#c0392b',fontWeight:'bold'}}>Are you sure you want to exit?</h3>
            <p style={{fontSize:'14px',color:'#666',marginBottom:'20px',lineHeight:'1.5'}}>Your progress will be saved. You can resume this exam later from where you left off.</p>
            <div style={{display:'flex',gap:'10px',justifyContent:'flex-end'}}>
              <button onClick={()=>setExitDlg(false)} style={{padding:'9px 20px',border:`1px solid ${S.blue}`,background:'#fff',color:S.blue,cursor:'pointer',fontSize:'13px',fontWeight:'bold',borderRadius:'4px',fontFamily:S.font}} data-testid="cancel-exit">Continue Exam</button>
              <button onClick={doExit} disabled={exiting} style={{padding:'9px 20px',background:'#c0392b',color:'#fff',border:'none',cursor:'pointer',fontSize:'13px',fontWeight:'bold',borderRadius:'4px',fontFamily:S.font,opacity:exiting?0.7:1}} data-testid="confirm-exit">{exiting?'Saving…':'Exit Exam'}</button>
            </div>
          </div>
        </div>
      )}
      {/* Submit Confirmation */}
      {submitDlg && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:50}} data-testid="submit-dialog">
          <div style={{background:'#fff',borderRadius:'10px',padding:'28px',maxWidth:'440px',width:'90%',boxShadow:'0 8px 32px rgba(0,0,0,0.2)'}}>
            <h3 style={{margin:'0 0 8px',fontSize:'18px',color:'#27ae60',fontWeight:'bold'}}>Submit Your Exam?</h3>
            <p style={{fontSize:'14px',color:'#666',marginBottom:'8px',lineHeight:'1.5'}}>
              You have completed all sections{targetSkill ? ` for ${targetSkill}` : ''}. Would you like to submit your responses and view your results?
            </p>
            <p style={{fontSize:'12px',color:'#999',marginBottom:'20px'}}>Your answers will be scored and you'll see the answer key.</p>
            <div style={{display:'flex',gap:'10px',justifyContent:'flex-end'}}>
              <button onClick={()=>setSubmitDlg(false)} style={{padding:'9px 20px',border:'1px solid #ccc',background:'#fff',color:'#666',cursor:'pointer',fontSize:'13px',fontWeight:'bold',borderRadius:'4px',fontFamily:S.font}}>Go Back</button>
              <button onClick={doSubmit} disabled={submitting} style={{padding:'9px 24px',background:'#27ae60',color:'#fff',border:'none',cursor:'pointer',fontSize:'13px',fontWeight:'bold',borderRadius:'4px',fontFamily:S.font,opacity:submitting?0.6:1}} data-testid="confirm-submit">
                {submitting ? 'Submitting...' : 'Submit & View Results'}
              </button>
            </div>
          </div>
        </div>
      )}
      <style>{`@keyframes spin{to{transform:rotate(360deg);}} @keyframes pulse{0%,100%{opacity:1;}50%{opacity:0.4;}}`}</style>
    </div>
    </ProtectedContent>
  );
}
